// @ts-check
import { angularDimensionLayout, angularDimensionPlacement } from './angular_dimension_geometry.mjs';
import { renderAngularDimension } from './angular_dimension_render.mjs';
import { getPlanRenderTarget } from '../render/plan_capture_context.mjs';

import { createSketchWallGeometryContext } from '../../plugin-sdk/sketch_wall_geometry.js';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { on } from '../utils/event_bus.js?v=20260706d';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { recordUndo } from './history_service.js?v=20260903c';
import {
  PLAN_LABEL_SCALE_MODES,
  resolvePlanLabelMetrics
} from '../render/plan_label_options.js?v=20260828b';
import { isPrintPlanCaptureInProgress } from '../print/print_plan_context.js?v=20260826b';
import {
  drawingObjectState,
  drawingObjectWorldCorners
} from '../objects/drawing_object_registry.js?v=20260904f';
import {
  SKETCH_WALL_SYSTEM_ID,
  allSketchWalls
} from '../sketch_walls/sketch_wall_state.js?v=20260904f';

const DIMENSION_TOOL = 'dimension';
export const ANGULAR_DIMENSION_TOOL = 'angular_dimension';
const DEFAULT_UNIT = 'mm';
const DEFAULT_PRECISION = 0;
const DIMENSION_LABEL_REFERENCE_FONT_PX = 12;
const DIMENSION_ARROW_REFERENCE_LENGTH_PX = 11;
const DIMENSION_ARROW_REFERENCE_WIDTH_PX = 7;
const DIMENSION_LABEL_OFFSET_REFERENCE_PX = 6;
const SNAP_TOLERANCE_PX = 12;
const HANDLE_TOLERANCE_PX = 12;
const DIMENSION_ALIGNMENT_SNAP_TOLERANCE_PX = 10;
const PARALLEL_TOLERANCE_RAD = 4 * Math.PI / 180;
const MIN_VECTOR_LENGTH = 0.0001;
const SYSTEM_PLAN_LAYER_SELECTOR = '.product-system-plan-layer[data-system-id]';
const SNAP_ROLE = Object.freeze({
  CORNER: 'corner',
  EDGE: 'edge',
  MIDPOINT: 'midpoint',
  CENTER: 'center',
  FREE: 'free'
});

const snapCandidateCaches = new Map();
let referenceEntryCache = null;
let dimensionGeometryRevision = 0;

function nextDimensionGeometryRevision() {
  dimensionGeometryRevision = dimensionGeometryRevision >= Number.MAX_SAFE_INTEGER
    ? 1
    : dimensionGeometryRevision + 1;
  return dimensionGeometryRevision;
}

function numeric(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function vec(value = {}) {
  return {
    x: numeric(value.x),
    z: numeric(value.z ?? value.y)
  };
}

export function add(a, b) {
  return { x: numeric(a?.x) + numeric(b?.x), z: numeric(a?.z ?? a?.y) + numeric(b?.z ?? b?.y) };
}

export function sub(a, b) {
  return { x: numeric(a?.x) - numeric(b?.x), z: numeric(a?.z ?? a?.y) - numeric(b?.z ?? b?.y) };
}

export function mul(v, scalar) {
  return { x: numeric(v?.x) * numeric(scalar), z: numeric(v?.z ?? v?.y) * numeric(scalar) };
}

export function dot(a, b) {
  return numeric(a?.x) * numeric(b?.x) + numeric(a?.z ?? a?.y) * numeric(b?.z ?? b?.y);
}

export function length(v) {
  return Math.hypot(numeric(v?.x), numeric(v?.z ?? v?.y));
}

export function distance(a, b) {
  return length(sub(a, b));
}

export function normalize(v) {
  const len = length(v);
  if (len < MIN_VECTOR_LENGTH) return { x: 1, z: 0 };
  return { x: numeric(v?.x) / len, z: numeric(v?.z ?? v?.y) / len };
}

export function perpendicular(v) {
  return { x: -numeric(v?.z ?? v?.y), z: numeric(v?.x) };
}

function clamp01(value) {
  return Math.max(0, Math.min(1, numeric(value)));
}

function segmentParameter(point, a, b) {
  const ab = sub(b, a);
  const denom = dot(ab, ab);
  if (denom < MIN_VECTOR_LENGTH) return 0;
  return clamp01(dot(sub(point, a), ab) / denom);
}

function pointOnSegmentAt(a, b, t) {
  return add(a, mul(sub(b, a), clamp01(t)));
}

function rotatePoint(point, origin, angleDeg = 0) {
  const angle = (numeric(angleDeg) * Math.PI) / 180;
  if (Math.abs(angle) < 0.000001) return vec(point);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = numeric(point?.x) - numeric(origin?.x);
  const dz = numeric(point?.z ?? point?.y) - numeric(origin?.z ?? origin?.y);
  return {
    x: numeric(origin?.x) + dx * cos - dz * sin,
    z: numeric(origin?.z ?? origin?.y) + dx * sin + dz * cos
  };
}

function rotateVector(point, angleDeg = 0) {
  return rotatePoint(point, { x: 0, z: 0 }, angleDeg);
}

export function closestPointOnSegment(p, a, b) {
  const ab = sub(b, a);
  const denom = dot(ab, ab);
  if (denom < MIN_VECTOR_LENGTH) return vec(a);
  const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / denom));
  return add(a, mul(ab, t));
}

export function distancePointToSegment(p, a, b) {
  return distance(p, closestPointOnSegment(p, a, b));
}

export function areSegmentsParallel(a1, a2, b1, b2, toleranceRadians = PARALLEL_TOLERANCE_RAD) {
  const av = normalize(sub(a2, a1));
  const bv = normalize(sub(b2, b1));
  const angle = Math.acos(Math.max(-1, Math.min(1, Math.abs(dot(av, bv)))));
  return angle <= toleranceRadians;
}

export function formatDimensionValue(valueInDocumentUnits, options = {}) {
  if (options.mode === 'angular') {
    const precision = Math.max(0, Math.min(4, Math.round(numeric(options.precision, 1))));
    return `${numeric(valueInDocumentUnits).toFixed(precision)}°`;
  }
  const unit = String(options.unit || DEFAULT_UNIT);
  const precision = Math.max(0, Math.min(4, Math.round(numeric(options.precision, DEFAULT_PRECISION))));
  const scale = numeric(options.scale, 1) || 1;
  const value = numeric(valueInDocumentUnits) * scale;
  if (unit === 'm') return `${(value / 1000).toFixed(Math.max(2, precision))} m`;
  if (unit === 'cm') return `${(value / 10).toFixed(Math.max(1, precision))} cm`;
  return `${value.toFixed(precision)} ${unit}`;
}

function axisForMode(pointA, pointB, mode = 'aligned') {
  if (mode === 'horizontal') return { x: 1, z: 0 };
  if (mode === 'vertical') return { x: 0, z: 1 };
  return normalize(sub(pointB, pointA));
}

function axisForDimension(dimension, pointA, pointB, resolved = null) {
  const mode = dimension?.mode || 'aligned';
  const baseAxis = resolved?.baseAxis || null;
  if (mode === 'horizontal') return baseAxis ? normalize(baseAxis) : { x: 1, z: 0 };
  if (mode === 'vertical') return baseAxis ? normalize(perpendicular(baseAxis)) : { x: 0, z: 1 };
  return axisForMode(pointA, pointB, mode);
}

function normalForDimensionAxis(axis, mode = 'aligned') {
  const normal = normalize(perpendicular(axis));
  return mode === 'vertical' ? mul(normal, -1) : normal;
}

function valueForMode(pointA, pointB, mode = 'aligned', axis = null) {
  if (mode === 'horizontal' || mode === 'vertical') return Math.abs(dot(sub(pointB, pointA), normalize(axis || axisForMode(pointA, pointB, mode))));
  return distance(pointA, pointB);
}

export function dimensionLayout(dimension, pointAOverride = null, pointBOverride = null, resolved = null) {
  if (dimension?.mode === 'angular') return angularDimensionLayout(dimension, resolved || {});
  const pointA = vec(pointAOverride || dimension?.pointA);
  const pointB = vec(pointBOverride || dimension?.pointB);
  const mode = dimension?.mode || 'aligned';
  const axis = axisForDimension(dimension, pointA, pointB, resolved);
  const normal = normalForDimensionAxis(axis, mode);
  const offset = numeric(dimension?.offset);
  const dimA = add(pointA, mul(normal, offset));
  const dimB = mode === 'horizontal' || mode === 'vertical'
    ? add(dimA, mul(axis, dot(sub(pointB, pointA), axis)))
    : add(pointB, mul(normal, offset));
  const textPosition = add(dimA, mul(sub(dimB, dimA), 0.5));
  return {
    pointA,
    pointB,
    axis: normalize(sub(dimB, dimA)),
    normal,
    dimA,
    dimB,
    textPosition,
    value: valueForMode(pointA, pointB, mode, axis)
  };
}

function ensureDimensionState() {
  if (!S.project || typeof S.project !== 'object') S.project = {};
  if (!Array.isArray(S.project.dimensions)) S.project.dimensions = [];
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  return S.project.dimensions;
}

function normalizePointLike(value, fallback = { x: 0, z: 0 }) {
  const point = vec(value || fallback);
  if (!Number.isFinite(point.x) || !Number.isFinite(point.z)) return vec(fallback);
  return point;
}

function optionalPointLike(value) {
  if (!value || typeof value !== 'object') return null;
  const x = Number(value.x);
  const z = Number(value.z ?? value.y);
  return Number.isFinite(x) && Number.isFinite(z) ? { x, z } : null;
}

function normalizedRefOwner(ref = {}) {
  try {
    const metadata = ref?.metadata && typeof ref.metadata === 'object' && !Array.isArray(ref.metadata)
      ? ref.metadata
      : null;
    const ownerFields = ['systemType', 'systemId', 'systemKey', 'owner', 'ownerId'];
    const owners = [];
    for (const source of [ref, metadata]) {
      if (!source) continue;
      for (const field of ownerFields) {
        const value = source[field];
        if (value === null || value === undefined || value === '') continue;
        if (!['string', 'number'].includes(typeof value)) return { valid: false, owner: '' };
        const normalized = String(value).trim();
        if (normalized) owners.push(normalized);
      }
    }
    const uniqueOwners = [...new Set(owners)];
    return {
      valid: uniqueOwners.length <= 1,
      owner: uniqueOwners[0] || ''
    };
  } catch (_error) {
    return { valid: false, owner: '' };
  }
}

export function dimensionOwnerForSvgElement(element) {
  let owner = 'core';
  let declaredCoreOwner = '';
  let current = element;
  while (current) {
    if (!declaredCoreOwner) {
      declaredCoreOwner = String(current.getAttribute?.('data-dimension-owner-id') || '').trim();
    }
    if (current.matches?.(SYSTEM_PLAN_LAYER_SELECTOR)) {
      const candidate = String(current.getAttribute?.('data-system-id') || '').trim();
      if (candidate) owner = candidate;
    }
    current = current.parentElement || current.parentNode || null;
  }
  return owner === 'core' && declaredCoreOwner ? declaredCoreOwner : owner;
}

function normalizeRefMetadata(ref = {}) {
  const owner = normalizedRefOwner(ref);
  if (!owner.valid) return null;
  const metadata = {};
  if (owner.owner) {
    metadata.systemType = owner.owner;
    metadata.systemId = owner.owner;
  }
  ['groupId', 'sourceId'].forEach(key => {
    const value = String(ref[key] || '').trim();
    if (value) metadata[key] = value;
  });
  return metadata;
}

export function normalizeGeometryRef(ref) {
  if (!ref || typeof ref !== 'object') return null;
  const type = String(ref.type || '').trim();
  const entityId = String(ref.entityId || '').trim();
  const role = String(ref.role || ref.snapRole || '').trim();
  if (!type) return null;
  const localSpace = ['center', 'pivot'].includes(ref.localSpace) ? ref.localSpace : '';
  const keepLocal = Boolean(localSpace);
  const metadata = normalizeRefMetadata(ref);
  if (!metadata) return null;
  if (type === 'segment') {
    const anchorT = Number(ref.t ?? ref.anchorT);
    return {
      entityId,
      type,
      index: Math.max(0, Math.round(numeric(ref.index))),
      fallbackA: normalizePointLike(ref.fallbackA),
      fallbackB: normalizePointLike(ref.fallbackB),
      localA: keepLocal ? optionalPointLike(ref.localA) : null,
      localB: keepLocal ? optionalPointLike(ref.localB) : null,
      baseAxis: optionalPointLike(ref.baseAxis),
      ...(localSpace ? { localSpace } : {}),
      role: role || SNAP_ROLE.EDGE,
      ...metadata,
      ...(Number.isFinite(anchorT) ? { t: clamp01(anchorT) } : {})
    };
  }
  if (type === 'bbox-corner' || type === 'svg-corner') {
    return {
      entityId,
      type,
      corner: ['tl', 'tr', 'br', 'bl'].includes(ref.corner) ? ref.corner : 'tl',
      fallbackPoint: normalizePointLike(ref.fallbackPoint),
      localPoint: keepLocal ? optionalPointLike(ref.localPoint) : null,
      baseAxis: optionalPointLike(ref.baseAxis),
      ...(localSpace ? { localSpace } : {}),
      role: role || SNAP_ROLE.CORNER,
      ...metadata
    };
  }
  if (type === 'center') {
    return {
      entityId,
      type,
      index: 0,
      fallbackPoint: normalizePointLike(ref.fallbackPoint || ref.fallback),
      localPoint: keepLocal ? optionalPointLike(ref.localPoint) : null,
      baseAxis: optionalPointLike(ref.baseAxis),
      ...(localSpace ? { localSpace } : {}),
      role: role || SNAP_ROLE.CENTER,
      ...metadata
    };
  }
  if (type === 'midpoint') {
    return {
      entityId,
      type,
      index: Math.max(0, Math.round(numeric(ref.index))),
      fallbackPoint: normalizePointLike(ref.fallbackPoint || ref.fallback),
      localPoint: keepLocal ? optionalPointLike(ref.localPoint) : null,
      baseAxis: optionalPointLike(ref.baseAxis),
      ...(localSpace ? { localSpace } : {}),
      role: role || SNAP_ROLE.MIDPOINT,
      ...metadata
    };
  }
  return {
    entityId,
    type: type === 'vertex' ? 'vertex' : 'free-point',
    index: Math.max(0, Math.round(numeric(ref.index))),
    fallbackPoint: normalizePointLike(ref.fallbackPoint || ref.fallback),
    localPoint: keepLocal ? optionalPointLike(ref.localPoint) : null,
    baseAxis: optionalPointLike(ref.baseAxis),
    ...(localSpace ? { localSpace } : {}),
    role: role || (entityId ? SNAP_ROLE.CENTER : SNAP_ROLE.FREE),
    ...metadata
  };
}

export function normalizeDimensionEntity(raw = {}) {
  const pointA = normalizePointLike(raw.pointA);
  const pointB = normalizePointLike(raw.pointB, { x: pointA.x + 1, z: pointA.z });
  const mode = ['aligned', 'horizontal', 'vertical', 'perpendicular', 'angular'].includes(raw.mode) ? raw.mode : 'aligned';
  return {
    id: String(raw.id || makeId('dimension')),
    type: 'dimension',
    mode,
    pointA,
    pointB,
    refA: normalizeGeometryRef(raw.refA),
    refB: normalizeGeometryRef(raw.refB),
    offset: numeric(raw.offset, 120),
    ...(mode === 'angular' ? { reflex: raw.reflex === true } : {}),
    textPosition: raw.textPosition ? normalizePointLike(raw.textPosition) : null,
    textOverride: typeof raw.textOverride === 'string' ? raw.textOverride : '',
    unit: raw.unit || DEFAULT_UNIT,
    precision: Math.max(0, Math.min(4, Math.round(numeric(raw.precision, DEFAULT_PRECISION)))),
    style: raw.style && typeof raw.style === 'object' ? { ...raw.style } : {},
    createdAt: numeric(raw.createdAt, Date.now()),
    updatedAt: numeric(raw.updatedAt, Date.now())
  };
}

export function normalizeProjectDimensions(project = S.project) {
  if (!project || typeof project !== 'object') return [];
  project.dimensions = Array.isArray(project.dimensions)
    ? project.dimensions.map(normalizeDimensionEntity).filter(dimension => dimension.mode === 'angular'
      ? Boolean(angularDimensionLayout(dimension))
      : distance(dimension.pointA, dimension.pointB) > MIN_VECTOR_LENGTH)
    : [];
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (S.selection.dimensionId && !project.dimensions.some(dimension => dimension.id === S.selection.dimensionId)) {
    S.selection.dimensionId = null;
  }
  return project.dimensions;
}

function boundsFromAny(entry = {}) {
  if (!entry || typeof entry !== 'object') return null;
  const bounds = entry.bounds || entry.metadata?.bounds || entry.metadata?.localBounds || entry;
  if (!bounds || typeof bounds !== 'object') return null;
  const minX = numeric(bounds.minX ?? bounds.x);
  const minZ = numeric(bounds.minZ ?? bounds.z);
  const maxX = Number.isFinite(Number(bounds.maxX)) ? Number(bounds.maxX) : minX + numeric(bounds.width);
  const maxZ = Number.isFinite(Number(bounds.maxZ)) ? Number(bounds.maxZ) : minZ + numeric(bounds.depth ?? bounds.height);
  if (maxX <= minX || maxZ <= minZ) return null;
  return { minX, minZ, maxX, maxZ };
}

function boundsCorners(bounds) {
  if (!bounds) return [];
  return [
    ['tl', { x: bounds.minX, z: bounds.minZ }],
    ['tr', { x: bounds.maxX, z: bounds.minZ }],
    ['br', { x: bounds.maxX, z: bounds.maxZ }],
    ['bl', { x: bounds.minX, z: bounds.maxZ }]
  ];
}

function currentDrawingEntry(entry = {}) {
  if (!entry || typeof entry !== 'object') return entry;
  const shouldUseDrawingObjectState = typeof entry.getState === 'function'
    || entry.systemType
    || entry.systemId
    || entry.owner;
  if (!shouldUseDrawingObjectState) return entry;
  const state = drawingObjectState(entry);
  if (!state || typeof state !== 'object') return entry;
  return {
    ...entry,
    ...state,
    metadata: {
      ...(entry.metadata || {}),
      ...(state.metadata || {})
    },
    getState: entry.getState
  };
}

function refBaseForEntry(entry = {}) {
  const base = {};
  const owner = normalizedRefOwner(entry);
  if (!owner.valid) return null;
  const systemType = owner.owner || 'core';
  const metadata = entry?.metadata || {};
  const groupId = String(metadata.groupId || metadata.sourceGroupId || metadata.connectedGroupId || '').trim();
  const sourceId = String(metadata.sourceId || entry.sourceId || entry.objectId || '').trim();
  base.systemType = systemType;
  base.systemId = systemType;
  if (groupId) base.groupId = groupId;
  if (sourceId) base.sourceId = sourceId;
  return base;
}

function validPoint(value) {
  return value && Number.isFinite(Number(value.x)) && Number.isFinite(Number(value.z ?? value.y));
}

function drawingObjectTransform(entry = {}, localBounds = null) {
  const localCenter = localBounds
    ? {
        x: (localBounds.minX + localBounds.maxX) / 2,
        z: (localBounds.minZ + localBounds.maxZ) / 2
      }
    : { x: 0, z: 0 };
  const position = validPoint(entry.position)
    ? { x: numeric(entry.position.x, localCenter.x), z: numeric(entry.position.z, localCenter.z) }
    : localCenter;
  const pivot = validPoint(entry.groupRotationPivot || entry.rotationPivot || entry.metadata?.rotationPivot)
    ? vec(entry.groupRotationPivot || entry.rotationPivot || entry.metadata?.rotationPivot)
    : null;
  const rotation = numeric(entry.rotation);
  return { localCenter, position, pivot, rotation };
}

function localPointToWorld(point, transform) {
  const local = vec(point);
  if (transform?.pivot) return rotatePoint(local, transform.pivot, transform.rotation);
  const translated = {
    x: local.x + transform.position.x - transform.localCenter.x,
    z: local.z + transform.position.z - transform.localCenter.z
  };
  return rotatePoint(translated, transform.position, transform.rotation);
}

function localBoundsPointToWorld(point, transform) {
  const local = vec(point);
  return rotatePoint(local, transform?.localCenter || local, numeric(transform?.rotation));
}

function dimensionLocalSpaceForEntry(entry = {}, transform = null) {
  const localSpace = String(entry?.metadata?.dimensionLocalSpace || entry?.dimensionLocalSpace || '').trim();
  if (localSpace === 'pivot' && transform?.pivot) return 'pivot';
  if (localSpace === 'center' && transform?.localCenter) return 'center';
  return '';
}

function basePointToWorld(point, transform, localSpace = '') {
  const basePoint = vec(point);
  if (localSpace === 'pivot' && transform?.pivot) {
    return rotatePoint(basePoint, transform.pivot, numeric(transform.rotation));
  }
  if (localSpace === 'center' && transform?.localCenter) {
    return rotatePoint(basePoint, transform.localCenter, numeric(transform.rotation));
  }
  return localPointToWorld(basePoint, transform);
}

function basePointToStableLocal(point, transform, localSpace = '') {
  const basePoint = vec(point);
  if (localSpace === 'pivot' && transform?.pivot) return sub(basePoint, vec(transform.pivot));
  if (localSpace === 'center' && transform?.localCenter) return sub(basePoint, vec(transform.localCenter));
  return worldPointToLocal(basePoint, transform);
}

function refLocalPointToWorld(point, transform, ref = {}) {
  const local = vec(point);
  if (ref?.localSpace === 'center') {
    return add(vec(transform?.localCenter), rotateVector(local, numeric(transform?.rotation)));
  }
  if (transform?.pivot && ref?.localSpace === 'pivot') {
    return add(vec(transform.pivot), rotateVector(local, transform.rotation));
  }
  return localPointToWorld(local, transform);
}

function localAxisToWorld(axis, transform) {
  const localAxis = normalize(axis || { x: 1, z: 0 });
  return normalize(rotatePoint(localAxis, { x: 0, z: 0 }, numeric(transform?.rotation)));
}

function worldPointToLocal(point, transform) {
  const world = vec(point);
  if (transform?.pivot) return rotatePoint(world, transform.pivot, -numeric(transform.rotation));
  const unrotated = rotatePoint(world, transform.position, -numeric(transform?.rotation));
  return {
    x: unrotated.x - numeric(transform?.position?.x) + numeric(transform?.localCenter?.x),
    z: unrotated.z - numeric(transform?.position?.z) + numeric(transform?.localCenter?.z)
  };
}

function worldPointToStableLocal(point, transform, options = {}) {
  const world = vec(point);
  const preferredLocalSpace = String(options.localSpace || '').trim();
  if (preferredLocalSpace === 'pivot' && transform?.pivot) {
    return {
      point: rotateVector(sub(world, transform.pivot), -numeric(transform.rotation)),
      localSpace: 'pivot'
    };
  }
  if (preferredLocalSpace === 'center' && transform?.localCenter) {
    return {
      point: rotateVector(sub(world, transform.localCenter), -numeric(transform.rotation)),
      localSpace: 'center'
    };
  }
  if (transform?.localCenter) {
    return {
      point: rotateVector(sub(world, transform.localCenter), -numeric(transform.rotation)),
      localSpace: 'center'
    };
  }
  if (transform?.pivot) {
    return {
      point: rotateVector(sub(world, transform.pivot), -numeric(transform.rotation)),
      localSpace: 'pivot'
    };
  }
  return { point: worldPointToLocal(world, transform), localSpace: '' };
}

function drawingObjectOrientedCorners(entry = {}) {
  if (!entry || typeof entry !== 'object') return null;
  const localBounds = entry.metadata?.localBounds
    ? boundsFromAny({ bounds: entry.metadata.localBounds })
    : null;
  if (!localBounds) return null;
  const transform = drawingObjectTransform(entry, localBounds);
  const localCorners = boundsCorners(localBounds);
  const localSpace = dimensionLocalSpaceForEntry(entry, transform);
  if (localSpace) {
    return localCorners.map(([corner, point]) => [
      corner,
      basePointToWorld(point, transform, localSpace),
      basePointToStableLocal(point, transform, localSpace),
      localSpace
    ]);
  }
  const worldCorners = drawingObjectWorldCorners(entry);
  return localCorners.map(([corner, point], index) => {
    const world = worldCorners.length >= 4
      ? worldCorners[index]
      : localBoundsPointToWorld(point, transform);
    const local = worldPointToStableLocal(world, transform);
    return [corner, world, local.point, local.localSpace];
  });
}

function entryCorners(entry = {}) {
  const oriented = drawingObjectOrientedCorners(entry);
  if (oriented?.length === 4) return oriented;
  const bounds = boundsFromAny(entry);
  return boundsCorners(bounds);
}

function addCornerSegmentCandidates(candidates, points, entityId, label = '', refTypePrefix = 'bbox', options = {}) {
  if (!Array.isArray(points) || points.length < 2 || !entityId) return;
  const refBase = options.refBase && typeof options.refBase === 'object' ? options.refBase : {};
  const baseAxis = optionalPointLike(options.baseAxis) ? normalize(options.baseAxis) : null;
  const pointEntryPoint = entry => vec(entry?.[1]);
  const pointEntryLocal = entry => (entry?.[2] ? vec(entry[2]) : pointEntryPoint(entry));
  const pointEntryLocalSpace = entry => String(entry?.[3] || options.localSpace || '').trim();
  const localRefAttrs = entry => {
    const localSpace = pointEntryLocalSpace(entry);
    return localSpace ? { localSpace } : {};
  };
  const commonLocalSpace = points.length && points.every(entry => pointEntryLocalSpace(entry) === pointEntryLocalSpace(points[0]))
    ? pointEntryLocalSpace(points[0])
    : String(options.localSpace || '').trim();
  points.forEach(entry => {
    const [corner, point] = entry;
    candidates.push({
      kind: 'point',
      point,
      entityId,
      label,
      role: SNAP_ROLE.CORNER,
      ref: { entityId, type: `${refTypePrefix}-corner`, corner, fallbackPoint: point, localPoint: pointEntryLocal(entry), baseAxis, ...localRefAttrs(entry), role: SNAP_ROLE.CORNER, ...refBase }
    });
  });
  const center = points.reduce((sum, entry) => add(sum, pointEntryPoint(entry)), { x: 0, z: 0 });
  center.x /= points.length;
  center.z /= points.length;
  const localCenter = points.reduce((sum, entry) => add(sum, pointEntryLocal(entry)), { x: 0, z: 0 });
  localCenter.x /= points.length;
  localCenter.z /= points.length;
  candidates.push({
    kind: 'point',
    point: center,
    entityId,
    label: label ? `${label} Zentrum` : 'Zentrum',
    role: SNAP_ROLE.CENTER,
    ref: { entityId, type: 'center', fallbackPoint: center, localPoint: localCenter, baseAxis, ...(commonLocalSpace ? { localSpace: commonLocalSpace } : {}), role: SNAP_ROLE.CENTER, ...refBase }
  });
  const isClosed = options.closed !== false;
  const segmentLimit = isClosed ? points.length : points.length - 1;
  const segments = Array.from({ length: segmentLimit }, (_, index) => [points[index], points[(index + 1) % points.length]]);
  segments.forEach(([entryA, entryB], index) => {
    const a = pointEntryPoint(entryA);
    const b = pointEntryPoint(entryB);
    if (distance(a, b) < MIN_VECTOR_LENGTH) return;
    candidates.push({
      kind: 'segment',
      a,
      b,
      entityId,
      label,
      role: SNAP_ROLE.EDGE,
      geometrySource: options.geometrySource || (refTypePrefix === 'bbox' ? 'bounds' : 'svg'),
      ref: { entityId, type: 'segment', index, fallbackA: a, fallbackB: b, localA: pointEntryLocal(entryA), localB: pointEntryLocal(entryB), baseAxis: normalize(sub(b, a)), ...localRefAttrs(entryA), role: SNAP_ROLE.EDGE, ...refBase }
    });
    const midpoint = add(a, mul(sub(b, a), 0.5));
    const localMidpoint = add(pointEntryLocal(entryA), mul(sub(pointEntryLocal(entryB), pointEntryLocal(entryA)), 0.5));
    candidates.push({
      kind: 'point',
      point: midpoint,
      entityId,
      label: label ? `${label} Mitte` : 'Mitte',
      role: SNAP_ROLE.MIDPOINT,
      ref: { entityId, type: 'midpoint', index, fallbackPoint: midpoint, localPoint: localMidpoint, baseAxis, ...localRefAttrs(entryA), role: SNAP_ROLE.MIDPOINT, ...refBase }
    });
  });
}

function addBoundsCandidates(candidates, entry, entityId, label = '') {
  const refBase = refBaseForEntry(entry);
  if (!refBase) return;
  const points = entryCorners(entry);
  const baseAxis = points.length >= 2 ? normalize(sub(points[1][1], points[0][1])) : null;
  if (points.length >= 2) addCornerSegmentCandidates(candidates, points, entityId, label, 'bbox', { closed: true, refBase, baseAxis });
  addCustomDimensionSnapPoints(candidates, entry, entityId, label, baseAxis);
  addCustomDimensionSnapSegments(candidates, entry, entityId, label, baseAxis);
}

// Generalisierte Gegenstueck zu addCustomDimensionSnapPoints, aber fuer eine
// beliebige (nicht notwendigerweise geschlossene/zusammenhaengende) Liste von
// Kanten -- gespeist aus entry.metadata.dimensionSnapSegments. Genutzt fuer
// importierte STEP-Objekte (Asset Import 2.0): dimension_geometry.json liefert
// die echten Kanten der Draufsicht-Projektion (nicht nur die 4 Ecken der
// Bounding Box), damit z.B. eine Bohrung oder ein L-Ausschnitt direkt bemassbar
// ist. Anders als addCornerSegmentCandidates (fortlaufende Eckenkette) koennen
// hier mehrere unzusammenhaengende Kantenzuege vorkommen, daher werden
// Eckpunkte ueber ihre Weltkoordinate dedupliziert statt aus einer einzigen
// Punktliste abgeleitet.
function addCustomDimensionSnapSegments(candidates, entry, entityId, label = '', baseAxis = null) {
  const snapSegments = Array.isArray(entry?.metadata?.dimensionSnapSegments) ? entry.metadata.dimensionSnapSegments : [];
  if (!snapSegments.length || !entityId) return;
  const localBounds = entry.metadata?.localBounds
    ? boundsFromAny({ bounds: entry.metadata.localBounds })
    : null;
  const transform = localBounds ? drawingObjectTransform(entry, localBounds) : null;
  const refBase = refBaseForEntry(entry);
  const localSpace = transform ? dimensionLocalSpaceForEntry(entry, transform) : '';
  const toWorld = basePoint => (transform && localSpace ? basePointToWorld(basePoint, transform, localSpace) : basePoint);
  const toStableLocal = (basePoint, worldPoint) => (transform && localSpace
    ? { point: basePointToStableLocal(basePoint, transform, localSpace), localSpace }
    : (transform ? worldPointToStableLocal(worldPoint, transform) : { point: worldPoint, localSpace: '' }));

  const uniqueCorners = new Map();
  const validSegments = [];
  snapSegments.forEach((snapSegment, index) => {
    const basePointA = optionalPointLike(snapSegment?.a);
    const basePointB = optionalPointLike(snapSegment?.b);
    if (!basePointA || !basePointB) return;
    const a = toWorld(basePointA);
    const b = toWorld(basePointB);
    if (distance(a, b) < MIN_VECTOR_LENGTH) return;
    validSegments.push({ index, basePointA, basePointB, a, b });
    [basePointA, basePointB].forEach(basePoint => {
      const world = toWorld(basePoint);
      const key = `${world.x.toFixed(3)}:${world.z.toFixed(3)}`;
      if (!uniqueCorners.has(key)) uniqueCorners.set(key, { basePoint, world });
    });
  });

  uniqueCorners.forEach(({ basePoint, world }) => {
    const stable = toStableLocal(basePoint, world);
    candidates.push({
      kind: 'point',
      point: world,
      entityId,
      label,
      role: SNAP_ROLE.CORNER,
      ref: {
        entityId, type: 'vertex', fallbackPoint: world, localPoint: stable.point, baseAxis,
        ...(stable.localSpace ? { localSpace: stable.localSpace } : {}),
        role: SNAP_ROLE.CORNER,
        ...refBase
      }
    });
  });

  validSegments.forEach(({ index, basePointA, basePointB, a, b }) => {
    const stableLocalA = toStableLocal(basePointA, a);
    candidates.push({
      kind: 'segment',
      a,
      b,
      entityId,
      label,
      role: SNAP_ROLE.EDGE,
      geometrySource: 'precise',
      ref: {
        entityId,
        type: 'segment',
        index,
        fallbackA: a,
        fallbackB: b,
        localA: stableLocalA.point,
        localB: toStableLocal(basePointB, b).point,
        baseAxis: baseAxis || normalize(sub(b, a)),
        ...(stableLocalA.localSpace ? { localSpace: stableLocalA.localSpace } : {}),
        role: SNAP_ROLE.EDGE,
        ...refBase
      }
    });
    const midpointBase = add(basePointA, mul(sub(basePointB, basePointA), 0.5));
    const midpointWorld = add(a, mul(sub(b, a), 0.5));
    const midpointStable = toStableLocal(midpointBase, midpointWorld);
    candidates.push({
      kind: 'point',
      point: midpointWorld,
      entityId,
      label: label ? `${label} Mitte` : 'Mitte',
      role: SNAP_ROLE.MIDPOINT,
      ref: {
        entityId,
        type: 'midpoint',
        index,
        fallbackPoint: midpointWorld,
        localPoint: midpointStable.point,
        baseAxis,
        ...(midpointStable.localSpace ? { localSpace: midpointStable.localSpace } : {}),
        role: SNAP_ROLE.MIDPOINT,
        ...refBase
      }
    });
  });
}

// Shared only within one synchronous preparation. Do not retain project state
// across revisions or use a product getter again for the second consumer.
function createDimensionSourceBatch() {
  const snapshot = SystemRegistry.collectDrawingObjectSnapshot();
  const states = new Map();
  let bounds = null;
  return {
    snapshot,
    current(entry, forcedOwner = '') {
      let variants = states.get(entry);
      if (!variants) { variants = new Map(); states.set(entry, variants); }
      if (!variants.has(forcedOwner)) {
        const source = forcedOwner
          ? { ...entry, owner: forcedOwner, systemId: forcedOwner, systemType: forcedOwner }
          : entry;
        variants.set(forcedOwner, currentDrawingEntry(source));
      }
      return variants.get(forcedOwner);
    },
    bounds() {
      if (!bounds) {
        try {
          const collected = SystemRegistry.collectProjectBounds();
          bounds = Array.isArray(collected) ? [...collected] : [];
        } catch { bounds = []; }
      }
      return bounds;
    }
  };
}

function currentDimensionReferenceEntries(projectState = S.project, batch = createDimensionSourceBatch()) {
  const drawingSnapshot = batch.snapshot;
  const cacheKey = `${drawingSnapshot?.cacheKey || ''}:${projectState === S.project ? 'current' : 'external'}`;
  if (referenceEntryCache
    && referenceEntryCache.project === projectState
    && referenceEntryCache.key === cacheKey) return referenceEntryCache.entries;
  const entries = [];
  const seen = new Set();
  const addReferenceEntry = (entry, fallbackId = '', forcedOwner = '') => {
    try {
      const current = batch.current(entry, forcedOwner);
      if (!current || typeof current !== 'object') return;
      const entityId = String(current.id || current.sourceId || current.objectId || fallbackId || '').trim();
      if (!entityId) return;
      const refBase = refBaseForEntry(current);
      if (!refBase) return;
      const owner = String(refBase.systemType || refBase.systemId || 'core').trim() || 'core';
      const canonicalKey = `${owner}\u0000${entityId}`;
      if (seen.has(canonicalKey)) return;
      const localBounds = current.metadata?.localBounds
        ? boundsFromAny({ bounds: current.metadata.localBounds })
        : boundsFromAny(current);
      if (!localBounds) return;
      seen.add(canonicalKey);
      entries.push({
        entry: current,
        entityId,
        owner,
        canonicalKey,
        refBase,
        localBounds,
        transform: drawingObjectTransform(current, localBounds)
      });
    } catch (_error) {
      // Ein fehlerhafter Plugin-Bounds-Eintrag darf andere Referenzen nicht blockieren.
    }
  };
  drawingSnapshot.objects.forEach((entry, index) => {
    addReferenceEntry(drawingSnapshot.statesByObject?.get?.(entry) || entry, `drawing-${index}`);
  });
  const projectBounds = batch.bounds();
  projectBounds.forEach((entry, index) => {
    let fallbackId = `bounds-${index}-${index}`;
    try {
      fallbackId = `bounds-${entry?.systemId || index}-${index}`;
    } catch (_error) {
      // Der neutrale Index bleibt ein sicherer Fallback.
    }
    addReferenceEntry(entry, fallbackId);
  });
  (projectState?.obstacles || []).forEach((entry, index) => addReferenceEntry(entry, `obstacle-${entry.id || index}`, 'core'));
  if (projectState?.floorplan) addReferenceEntry({ id: 'floorplan', label: 'Grundriss', bounds: projectState.floorplan }, 'floorplan', 'core');
  referenceEntryCache = { project: projectState, key: cacheKey, entries };
  return entries;
}

function sameStringId(a, b) {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  return Boolean(left && right && left === right);
}

function refsShareScope(a = {}, b = {}) {
  const ownerA = normalizedRefOwner(a);
  const ownerB = normalizedRefOwner(b);
  if (!ownerA.valid || !ownerB.valid) return false;
  if ((ownerA.owner || ownerB.owner) && ownerA.owner !== ownerB.owner) return false;
  const groupA = String(a.groupId || '').trim();
  const groupB = String(b.groupId || '').trim();
  const sourceA = String(a.sourceId || '').trim();
  const sourceB = String(b.sourceId || '').trim();
  return sameStringId(groupA, groupB)
    || sameStringId(sourceA, sourceB)
    || sameStringId(groupA, sourceB)
    || sameStringId(sourceA, groupB);
}

function svgEntityPayload(entityId) {
  const value = String(entityId || '');
  const match = value.match(/^svg:[^:]*:(.*):(rect|line|circle|ellipse|polygon|polyline):\d+$/i);
  return match?.[1] || '';
}

function entityIdMatchesAny(candidateEntityId, ref = {}) {
  const candidate = String(candidateEntityId || '').trim();
  if (!candidate) return false;
  const ids = [
    ref.entityId,
    ref.sourceId,
    ref.groupId,
    svgEntityPayload(ref.entityId)
  ].map(value => String(value || '').trim()).filter(Boolean);
  return ids.some(id => id === candidate);
}

function referenceEntryMatchScore(entryRef, ref) {
  if (!entryRef || !ref) return 0;
  const entryBase = entryRef.refBase || {};
  const refOwner = normalizedRefOwner(ref);
  const entryOwner = normalizedRefOwner(entryBase);
  if (!refOwner.valid || !entryOwner.valid) return 0;
  if (refOwner.owner && refOwner.owner !== entryOwner.owner) return 0;

  const refEntityPayload = svgEntityPayload(ref.entityId);
  const refEntityId = String(ref.entityId || '').trim();
  const refSourceId = String(ref.sourceId || '').trim();
  const refGroupId = String(ref.groupId || '').trim();
  const entryEntityId = String(entryRef.entityId || '').trim();
  const entrySourceId = String(entryBase.sourceId || '').trim();
  const entryGroupId = String(entryBase.groupId || '').trim();

  if (sameStringId(entryEntityId, refEntityId) || sameStringId(entryEntityId, refEntityPayload)) return 100;
  if (sameStringId(entrySourceId, refSourceId) || sameStringId(entrySourceId, refEntityPayload)) return 90;
  if (sameStringId(entryGroupId, refGroupId) || sameStringId(entryGroupId, refEntityPayload)) return 75;
  if (entityIdMatchesAny(entryEntityId, ref)) return 70;
  if (entityIdMatchesAny(entrySourceId, ref)) return 65;
  if (entityIdMatchesAny(entryGroupId, ref)) return 60;
  return refsShareScope(entryBase, ref) ? 45 : 0;
}

// Arrays belong to one exact reference-cache revision. Weak keys allow retired
// resolvers to keep their own immutable lookup without retaining old projects.
const referenceEntryLookups = new WeakMap();
function indexedReferenceEntries(entries, ref) {
  const owner = normalizedRefOwner(ref);
  if (!owner.valid) return [];
  let index = referenceEntryLookups.get(entries);
  if (!index) {
    index = new Map();
    entries.forEach((entry, order) => {
      const entryOwner = normalizedRefOwner(entry.refBase || {});
      if (!entryOwner.valid) return;
      const ids = new Set([entry.entityId, entry.refBase?.sourceId, entry.refBase?.groupId]
        .map(value => String(value || '').trim()).filter(Boolean));
      for (const id of ids) for (const scope of [null, entryOwner.owner || 'core']) {
        for (const kind of id === String(entry.entityId || '').trim() ? ['alias', 'entity'] : ['alias']) {
          const key = JSON.stringify([scope, kind, id]);
          const bucket = index.get(key) || [];
          bucket.push(order); index.set(key, bucket);
        }
      }
    });
    referenceEntryLookups.set(entries, index);
  }
  const exact = new Set();
  for (const value of [ref.entityId, svgEntityPayload(ref.entityId)]) {
    const id = String(value || '').trim();
    if (id) for (const order of index.get(JSON.stringify([owner.owner || null, 'entity', id])) || []) {
      if (referenceEntryMatchScore(entries[order], ref) === 100) exact.add(order);
    }
  }
  // Exact identity outranks every group/source alias, even when many objects
  // share a group. Keep all exact ties so owner ambiguity still fails closed.
  if (exact.size) return [...exact].sort((a, b) => a - b).map(order => entries[order]);
  const matches = new Set();
  for (const value of [ref.entityId, ref.sourceId, ref.groupId, svgEntityPayload(ref.entityId)]) {
    const id = String(value || '').trim();
    if (id) for (const order of index.get(JSON.stringify([owner.owner || null, 'alias', id])) || []) matches.add(order);
  }
  // Preserve the exact tie-breaking order of the previous full scan. The
  // existing score and cross-owner ambiguity checks remain authoritative.
  return [...matches].sort((a, b) => a - b).map(order => entries[order]);
}

function bestReferenceEntryForRef(ref, projectState = S.project, entriesOverride = null) {
  if (!ref) return null;
  let best = null;
  let bestScore = 0;
  let ambiguous = false;
  for (const entryRef of indexedReferenceEntries(entriesOverride || currentDimensionReferenceEntries(projectState), ref)) {
    const score = referenceEntryMatchScore(entryRef, ref);
    if (score > bestScore) {
      best = entryRef;
      bestScore = score;
      ambiguous = false;
    } else if (score > 0 && score === bestScore && best) {
      const bestOwner = String(best.refBase?.systemType || best.refBase?.systemId || 'core');
      const entryOwner = String(entryRef.refBase?.systemType || entryRef.refBase?.systemId || 'core');
      if (bestOwner !== entryOwner) ambiguous = true;
    }
  }
  return ambiguous ? null : best;
}

function refBaseForSvgElement(element, entityId, projectState = S.project, referenceEntries = null) {
  const sourceIds = [
    element.getAttribute?.('data-dimension-source-id'),
    element.closest?.('[data-dimension-source-id]')?.getAttribute('data-dimension-source-id'),
    element.getAttribute?.('data-object-asset-id'),
    element.closest?.('[data-object-asset-id]')?.getAttribute('data-object-asset-id'),
    element.getAttribute?.('data-obstacle-id'),
    element.closest?.('[data-obstacle-id]')?.getAttribute('data-obstacle-id'),
    element.getAttribute?.('data-group-id'),
    element.closest?.('[data-group-id]')?.getAttribute('data-group-id'),
    svgEntityPayload(entityId)
  ].map(value => String(value || '').trim()).filter(Boolean);
  const systemId = dimensionOwnerForSvgElement(element);
  const ref = {
    entityId,
    systemType: systemId,
    systemId,
    sourceId: sourceIds[0] || '',
    groupId: sourceIds[0] || ''
  };
  const entries = referenceEntries || currentDimensionReferenceEntries(projectState);
  const entryRef = entries.find(candidate => {
    const candidateOwner = normalizedRefOwner(candidate.refBase || {});
    if (!candidateOwner.valid || (candidateOwner.owner || 'core') !== systemId) return false;
    return sourceIds.some(id => (
      id === candidate.entityId
      || id === candidate.refBase.sourceId
      || id === candidate.refBase.groupId
    ));
  }) || bestReferenceEntryForRef(ref, projectState, entries);
  return entryRef || { refBase: normalizeRefMetadata(ref), transform: null, localBounds: null };
}

function decoratePointRefWithLocal(ref, point, svgRefBase) {
  if (!svgRefBase?.transform) return { ...ref, ...(svgRefBase?.refBase || {}) };
  const local = worldPointToStableLocal(point, svgRefBase.transform);
  return {
    ...ref,
    localPoint: local.point,
    ...(local.localSpace ? { localSpace: local.localSpace } : {}),
    ...(svgRefBase.refBase || {})
  };
}

function localizeSvgPointEntries(points, svgRefBase) {
  if (!svgRefBase?.transform) return points;
  return points.map(entry => {
    const point = entry?.[1];
    if (!point) return entry;
    const local = worldPointToStableLocal(point, svgRefBase.transform);
    return [entry[0], point, local.point, local.localSpace];
  });
}

function addCustomDimensionSnapPoints(candidates, entry, entityId, label = '', baseAxis = null) {
  const snapPoints = Array.isArray(entry?.metadata?.dimensionSnapPoints) ? entry.metadata.dimensionSnapPoints : [];
  if (!snapPoints.length || !entityId) return;
  const localBounds = entry.metadata?.localBounds
    ? boundsFromAny({ bounds: entry.metadata.localBounds })
    : null;
  const transform = localBounds ? drawingObjectTransform(entry, localBounds) : null;
  const refBase = refBaseForEntry(entry);
  snapPoints.forEach((snapPoint, index) => {
    const basePoint = optionalPointLike(snapPoint);
    if (!basePoint) return;
    const localSpace = transform ? dimensionLocalSpaceForEntry(entry, transform) : '';
    const point = transform && localSpace ? basePointToWorld(basePoint, transform, localSpace) : basePoint;
    const stableLocal = transform && localSpace
      ? { point: basePointToStableLocal(basePoint, transform, localSpace), localSpace }
      : (transform ? worldPointToStableLocal(point, transform) : { point, localSpace: '' });
    const role = String(snapPoint.role || '').trim() || SNAP_ROLE.CENTER;
    candidates.push({
      kind: 'point',
      point,
      entityId,
      label: snapPoint.label || label || 'Fangpunkt',
      role,
      ref: {
        entityId,
        type: 'vertex',
        index,
        fallbackPoint: point,
        localPoint: stableLocal.point,
        baseAxis,
        ...(stableLocal.localSpace ? { localSpace: stableLocal.localSpace } : {}),
        role,
        ...refBase
      }
    });
  });
}

function svgPointToWorld(point, svgRect = null) {
  const svg = getPlanRenderTarget();
  const rect = svgRect || svg?.getBoundingClientRect?.();
  if (!rect) return null;
  const scale = numeric(S.camera2D?.scale, 1) || 1;
  return {
    x: (numeric(point?.x) - rect.width / 2) / scale + numeric(S.camera2D?.x),
    z: (numeric(point?.y) - rect.height / 2) / scale + numeric(S.camera2D?.z)
  };
}

function transformedSvgPoint(element, x, y) {
  const svg = getPlanRenderTarget();
  const matrix = element?.getCTM?.();
  if (!svg || !matrix || typeof DOMPoint === 'undefined') return { x: numeric(x), y: numeric(y) };
  const point = new DOMPoint(numeric(x), numeric(y)).matrixTransform(matrix);
  return { x: point.x, y: point.y };
}

function svgElementEntityId(element, index) {
  const dimensionSourceId = element.getAttribute('data-dimension-source-id')
    || element.closest?.('[data-dimension-source-id]')?.getAttribute('data-dimension-source-id');
  const explicit = dimensionSourceId
    || element.id
    || element.getAttribute('data-object-asset-id')
    || element.closest?.('[data-object-asset-id]')?.getAttribute('data-object-asset-id')
    || element.getAttribute('data-obstacle-id')
    || element.closest?.('[data-obstacle-id]')?.getAttribute('data-obstacle-id')
    || element.getAttribute('data-group-id')
    || element.closest?.('[data-group-id]')?.getAttribute('data-group-id');
  const owner = dimensionOwnerForSvgElement(element);
  return `svg:${owner}:${explicit || element.tagName}:${element.tagName}:${index}`;
}

function shouldSkipSvgGeometryElement(element) {
  if (!element || element.closest?.('.dimension-layer')) return true;
  if (element.closest?.('.area-measurement-layer')) return true;
  if (element.closest?.('defs,clipPath,mask,pattern')) return true;
  if (element.closest?.('[data-dimension-ignore="true"]')) return true;
  const className = String(element.getAttribute('class') || '');
  return /\b(grid|floorplan|selection|handle|preview|snap|area-measurement|dimension|text-annotation)\b/i.test(className);
}

function collectSvgPrimitiveCandidates(projectState = S.project) {
  const svg = getPlanRenderTarget();
  const rect = svg?.getBoundingClientRect?.();
  if (!svg || !rect) return [];
  const candidates = [];
  const referenceEntries = currentDimensionReferenceEntries(projectState);
  const elements = [...svg.querySelectorAll('rect,line,circle,ellipse,polygon,polyline')].filter(element => !shouldSkipSvgGeometryElement(element));
  elements.forEach((element, index) => {
    const tag = element.tagName.toLowerCase();
    const entityId = svgElementEntityId(element, index);
    const svgRefBase = refBaseForSvgElement(element, entityId, projectState, referenceEntries);
    const label = element.getAttribute('data-dimension-source-label')
      || element.closest?.('[data-dimension-source-label]')?.getAttribute('data-dimension-source-label')
      || element.getAttribute('data-dimension-label')
      || element.getAttribute('aria-label')
      || tag;
    const pointFromSvg = (x, y) => svgPointToWorld(transformedSvgPoint(element, x, y), rect);
    if (tag === 'line') {
      const a = pointFromSvg(element.getAttribute('x1'), element.getAttribute('y1'));
      const b = pointFromSvg(element.getAttribute('x2'), element.getAttribute('y2'));
      if (a && b) addCornerSegmentCandidates(candidates, localizeSvgPointEntries([['a', a], ['b', b]], svgRefBase), entityId, label, 'svg', {
        closed: false,
        refBase: svgRefBase.refBase || {},
        baseAxis: normalize(sub(b, a))
      });
      return;
    }
    if (tag === 'circle' || tag === 'ellipse') {
      const center = pointFromSvg(element.getAttribute('cx'), element.getAttribute('cy'));
      if (!center) return;
      candidates.push({
        kind: 'point',
        point: center,
        entityId,
        label: `${label} Mittelpunkt`,
        role: SNAP_ROLE.CENTER,
        ref: decoratePointRefWithLocal({ entityId, type: 'center', fallbackPoint: center, role: SNAP_ROLE.CENTER }, center, svgRefBase)
      });
      return;
    }
    if (tag === 'rect') {
      const x = numeric(element.getAttribute('x'));
      const y = numeric(element.getAttribute('y'));
      const width = numeric(element.getAttribute('width'));
      const height = numeric(element.getAttribute('height'));
      if (!(width > 0) || !(height > 0)) return;
      const points = [
        ['tl', pointFromSvg(x, y)],
        ['tr', pointFromSvg(x + width, y)],
        ['br', pointFromSvg(x + width, y + height)],
        ['bl', pointFromSvg(x, y + height)]
      ].filter(([, point]) => point);
      addCornerSegmentCandidates(candidates, localizeSvgPointEntries(points, svgRefBase), entityId, label, 'svg', {
        closed: true,
        refBase: svgRefBase.refBase || {},
        baseAxis: points.length >= 2 ? normalize(sub(points[1][1], points[0][1])) : null
      });
      return;
    }
    const rawPoints = String(element.getAttribute('points') || '').trim();
    if (!rawPoints) return;
    const points = rawPoints
      .split(/\s+/)
      .map((pair, pointIndex) => {
        const [x, y] = pair.split(',').map(Number);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        return [String(pointIndex), pointFromSvg(x, y)];
      })
      .filter(Boolean);
    if (points.length >= 2) addCornerSegmentCandidates(candidates, localizeSvgPointEntries(points, svgRefBase), entityId, label, 'svg', {
      closed: tag === 'polygon',
      refBase: svgRefBase.refBase || {},
      baseAxis: normalize(sub(points[1][1], points[0][1]))
    });
  });
  return candidates;
}

function dimensionSnapCandidateCacheKey(projectState = S.project, drawingSnapshot = null, options = {}) {
  return [
    drawingSnapshot?.cacheKey || '',
    projectState?.currentView || '',
    Array.isArray(projectState?.obstacles) ? projectState.obstacles.length : 0,
    projectState?.floorplan ? 1 : 0,
    options.includeRenderedSvg === true ? 'rendered-svg' : 'model-only',
    options.preferDrawingObjects === true ? 'prefer-drawing-objects' : 'include-project-bounds'
  ].join('|');
}

function dimensionSnapCandidateCacheKind(options = {}) {
  if (options.includeRenderedSvg === true) return 'rendered-svg';
  return options.preferDrawingObjects === true ? 'model-only-prefer-drawing' : 'model-only';
}

export function invalidateDimensionSnapCandidateCache() {
  snapCandidateCaches.clear();
  referenceEntryCache = null;
  nextDimensionGeometryRevision();
}

function addSketchWallDimensionCandidates(candidates, projectState = S.project, options = {}) {
  const walls = projectState === S.project && options.readOnly !== true
    ? allSketchWalls()
    : (Array.isArray(projectState?.sketchWalls?.walls) ? projectState.sketchWalls.walls : []);
  if (!walls.length) return;
  const nodes = Array.isArray(projectState?.sketchWalls?.nodes) ? projectState.sketchWalls.nodes : [];
  const nodesById = new Map(nodes.map(node => [node.id, node]));
  const getPoint = nodeId => {
    const node = nodesById.get(nodeId);
    return node ? { x: numeric(node.x), z: numeric(node.z) } : { x: 0, z: 0 };
  };
  const geometryContext = createSketchWallGeometryContext({ nodes, walls, straightJoints: projectState?.sketchWalls?.straightJoints });
  for (const wall of walls) {
    if (!wall?.id) continue;
    const corners = geometryContext.corners(wall);
    const points = [
      ['start-inner', corners.startInner],
      ['end-inner', corners.endInner],
      ['end-outer', corners.endOuter],
      ['start-outer', corners.startOuter]
    ];
    const start = getPoint(wall.startNodeId);
    const end = getPoint(wall.endNodeId);
    addCornerSegmentCandidates(candidates, points, wall.id, 'Parametrische Wand', 'wall', {
      closed: true,
      geometrySource: 'precise',
      baseAxis: normalize(sub(end, start)),
      refBase: {
        systemType: SKETCH_WALL_SYSTEM_ID,
        systemId: SKETCH_WALL_SYSTEM_ID,
        sourceId: wall.id,
        groupId: wall.id
      }
    });
  }
}

function candidateIndexParts(ref = {}) {
  const normalizedOwner = normalizedRefOwner(ref);
  if (!normalizedOwner.valid) return null;
  const ids = [];
  for (const value of [ref.entityId, ref.sourceId, ref.groupId, svgEntityPayload(ref.entityId)]) {
    const id = String(value || '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  }
  const type = String(ref.type || '');
  const detail = type === 'segment' || type === 'midpoint'
    ? String(Number(ref.index) || 0)
    : String(ref.corner ?? (Number(ref.index) || 0));
  return { owner: normalizedOwner.owner, ids, type, detail };
}

function candidateIndexKeys(ref = {}, options = {}) {
  const parts = candidateIndexParts(ref);
  if (!parts) return [];
  const { ids, type, detail } = parts;
  const owner = options.wildcardOwner === true
    ? '*'
    : (parts.owner || options.defaultOwner || '*');
  return [...new Set(ids.flatMap(id => [
    `${owner}|${id}|${type}|${detail}`,
    `${owner}|${id}|*|*`
  ]))];
}

function appendCandidateReferenceEntry(index, key, candidate) {
  const previous = index.get(key);
  if (!previous) {
    // Store singleton keys directly; allocate a bucket only on a tie.
    index.set(key, candidate);
  } else if (Array.isArray(previous)) {
    if (previous[previous.length - 1] !== candidate) previous.push(candidate);
  } else if (previous !== candidate) {
    index.set(key, [previous, candidate]);
  }
}

function buildCandidateReferenceIndex(candidates = []) {
  const index = new Map();
  for (const candidate of candidates) {
    if (!candidate) continue;
    const parts = candidateIndexParts(candidate.ref || {});
    if (!parts) continue;
    const { ids, type, detail } = parts;
    const owner = parts.owner || 'core';
    // Retain the legacy key strings, including collisions and wildcard-owner
    // lookup. Prepare aliases once rather than once per owner variant.
    for (let pass = 0; pass < (owner === '*' ? 1 : 2); pass += 1) {
      const indexedOwner = pass === 0 ? owner : '*';
      for (const id of ids) {
        appendCandidateReferenceEntry(index, `${indexedOwner}|${id}|${type}|${detail}`, candidate);
        appendCandidateReferenceEntry(index, `${indexedOwner}|${id}|*|*`, candidate);
      }
    }
  }
  return index;
}

function spatialCellKey(x, z) {
  return `${x}:${z}`;
}

function buildCandidateSpatialIndex(candidates = [], cellSize = 1000) {
  const cells = new Map();
  const overflow = [];
  const addToCell = (key, candidate) => {
    const entries = cells.get(key) || [];
    entries.push(candidate);
    cells.set(key, entries);
  };
  for (const candidate of candidates) {
    const points = candidate?.kind === 'segment'
      ? [candidate.a, candidate.b]
      : candidate?.kind === 'point'
        ? [candidate.point]
        : [];
    if (!points.length) continue;
    const minX = Math.min(...points.map(entry => numeric(entry?.x)));
    const maxX = Math.max(...points.map(entry => numeric(entry?.x)));
    const minZ = Math.min(...points.map(entry => numeric(entry?.z ?? entry?.y)));
    const maxZ = Math.max(...points.map(entry => numeric(entry?.z ?? entry?.y)));
    const x0 = Math.floor(minX / cellSize);
    const x1 = Math.floor(maxX / cellSize);
    const z0 = Math.floor(minZ / cellSize);
    const z1 = Math.floor(maxZ / cellSize);
    if ((x1 - x0 + 1) * (z1 - z0 + 1) > 64) {
      overflow.push(candidate);
      continue;
    }
    for (let x = x0; x <= x1; x += 1) {
      for (let z = z0; z <= z1; z += 1) addToCell(spatialCellKey(x, z), candidate);
    }
  }
  return { cellSize, cells, overflow };
}

function queryCandidateSpatialIndex(index, bounds) {
  if (!index || !bounds) return [];
  const x0 = Math.floor(numeric(bounds.minX) / index.cellSize);
  const x1 = Math.floor(numeric(bounds.maxX) / index.cellSize);
  const z0 = Math.floor(numeric(bounds.minZ) / index.cellSize);
  const z1 = Math.floor(numeric(bounds.maxZ) / index.cellSize);
  const result = new Set(index.overflow);
  for (let x = x0; x <= x1; x += 1) {
    for (let z = z0; z <= z1; z += 1) {
      for (const candidate of index.cells.get(spatialCellKey(x, z)) || []) result.add(candidate);
    }
  }
  return [...result];
}

function queryCachedDimensionCandidates(cache, bounds) {
  if (!cache || !bounds) return [];
  if (!cache.spatialIndex) cache.spatialIndex = buildCandidateSpatialIndex(cache.candidates);
  return queryCandidateSpatialIndex(cache.spatialIndex, bounds);
}

export function getDimensionGeometryRevision() {
  return dimensionGeometryRevision;
}

export function collectDimensionSnapCandidates(projectState = S.project, options = {}, batch = createDimensionSourceBatch()) {
  const drawingSnapshot = batch.snapshot;
  const cacheKind = dimensionSnapCandidateCacheKind(options);
  const key = dimensionSnapCandidateCacheKey(projectState, drawingSnapshot, options);
  const cached = snapCandidateCaches.get(cacheKind);
  if (
    options.force !== true
    && cached
    && cached.project === projectState
    && cached.key === key
  ) {
    return cached.candidates;
  }
  const candidates = [];
  const seen = new Set();
  const addEntry = (entry, fallbackId, forcedOwner = '') => {
    try {
      const current = batch.current(entry, forcedOwner);
      if (!current || typeof current !== 'object') return;
      const entityId = String(current.id || current.sourceId || current.objectId || fallbackId || '').trim();
      const refBase = refBaseForEntry(current);
      if (!entityId || !refBase) return;
      const owner = String(refBase.systemType || refBase.systemId || 'core').trim() || 'core';
      const canonicalKey = `${owner}\u0000${entityId}`;
      if (seen.has(canonicalKey)) return;
      const entryCandidates = [];
      addBoundsCandidates(entryCandidates, current, entityId, current.label || current.name || current.type || '');
      seen.add(canonicalKey);
      candidates.push(...entryCandidates);
    } catch (_error) {
      // Plugin-Geometrie wird pro Eintrag isoliert; gesunde Systeme laufen weiter.
    }
  };

  const drawingSystemIds = new Set();
  drawingSnapshot.objects.forEach((entry, index) => {
    const current = drawingSnapshot.statesByObject?.get?.(entry) || entry;
    const systemId = String(current?.systemType || current?.systemId || '').trim();
    if (systemId) drawingSystemIds.add(systemId);
    addEntry(current, `drawing-${index}`);
  });
  const projectBounds = batch.bounds();
  projectBounds.forEach((entry, index) => {
    try {
      if (options.preferDrawingObjects === true
        && drawingSystemIds.has(String(entry?.systemId || '').trim())) return;
      addEntry(entry, `bounds-${entry?.systemId || index}-${index}`);
    } catch (_error) {
      // Ein nicht lesbarer Bounds-Eintrag wird verworfen.
    }
  });
  (projectState?.obstacles || []).forEach((entry, index) => addEntry(entry, `obstacle-${entry.id || index}`, 'core'));
  if (projectState?.floorplan) addEntry({ id: 'floorplan', label: 'Grundriss', bounds: projectState.floorplan }, 'floorplan', 'core');
  addSketchWallDimensionCandidates(candidates, projectState, options);
  if (options.includeRenderedSvg === true) candidates.push(...collectSvgPrimitiveCandidates(projectState));
  const entry = {
    project: projectState,
    key,
    geometryKey: dimensionSnapCandidateCacheKey(projectState, drawingSnapshot),
    candidates,
    // Pointer snapping needs only the spatial index. Saved references build
    // their separate lookup on demand, once for this exact cache entry.
    referenceIndex: null,
    spatialIndex: null
  };
  snapCandidateCaches.set(cacheKind, entry);
  nextDimensionGeometryRevision();
  return candidates;
}

export function queryDimensionSnapCandidates(bounds, projectState = S.project, options = {}) {
  const cacheKind = dimensionSnapCandidateCacheKind(options);
  collectDimensionSnapCandidates(projectState, options);
  const cache = snapCandidateCaches.get(cacheKind);
  return queryCachedDimensionCandidates(cache, bounds);
}

function candidateMatchesRef(candidate, ref) {
  if (!candidate?.ref || !ref) return false;
  const candidateOwner = normalizedRefOwner(candidate.ref);
  const refOwner = normalizedRefOwner(ref);
  if (!candidateOwner.valid || !refOwner.valid) return false;
  if (refOwner.owner && (candidateOwner.owner || 'core') !== refOwner.owner) return false;
  const sameEntity = entityIdsMatch(candidate.ref.entityId, ref.entityId);
  const hasLocalRef = Boolean(ref.localPoint || (ref.localA && ref.localB));
  const sameScope = !hasLocalRef && refsShareScope(candidate.ref, ref);
  if ((!sameEntity && !sameScope) || !refTypesMatch(candidate.ref.type, ref.type)) return false;
  const candidateType = String(candidate.ref.type || '');
  const refType = String(ref.type || '');
  if (refType === 'segment') return Number(candidate.ref.index) === Number(ref.index);
  if (ref.type === 'bbox-corner' || ref.type === 'svg-corner') return candidate.ref.corner === ref.corner;
  if (refType === 'midpoint') return Number(candidate.ref.index) === Number(ref.index);
  if (candidateType === 'bbox-corner' || candidateType === 'svg-corner') return candidate.ref.corner === ref.corner;
  return Number(candidate.ref.index || 0) === Number(ref.index || 0);
}

function indexedCandidatesForRef(ref, cache) {
  if (!ref || !cache) return [];
  const keys = candidateIndexKeys(ref);
  if (!keys.length) return [];
  if (!cache.referenceIndex) cache.referenceIndex = buildCandidateReferenceIndex(cache.candidates);
  const matches = new Set();
  for (const key of keys) {
    const entry = cache.referenceIndex.get(key);
    if (Array.isArray(entry)) {
      for (const candidate of entry) matches.add(candidate);
    } else if (entry) {
      matches.add(entry);
    }
  }
  return [...matches];
}

function entityIdsMatch(candidateEntityId, refEntityId) {
  const candidate = String(candidateEntityId || '');
  const ref = String(refEntityId || '');
  if (candidate === ref) return true;
  const payload = svgEntityPayload(ref);
  if (payload && candidate === payload) return true;
  return false;
}

function unambiguousCandidateMatch(candidates, ref) {
  const matches = candidates.filter(candidate => candidateMatchesRef(candidate, ref));
  if (!matches.length) return null;
  const refOwner = normalizedRefOwner(ref);
  if (!refOwner.valid) return null;
  if (!refOwner.owner) {
    const owners = new Set(matches.map(candidate => {
      const candidateOwner = normalizedRefOwner(candidate.ref || {});
      return candidateOwner.valid ? (candidateOwner.owner || 'core') : '';
    }).filter(Boolean));
    if (owners.size !== 1) return null;
  }
  return matches[0];
}

function refTypesMatch(candidateType, refType) {
  if (candidateType === refType) return true;
  return (candidateType === 'bbox-corner' && refType === 'svg-corner')
    || (candidateType === 'svg-corner' && refType === 'bbox-corner');
}

function resolveGeometryRefFromLocal(ref, projectState = S.project, entries = null) {
  const entryRef = bestReferenceEntryForRef(ref, projectState, entries);
  if (!entryRef?.transform) return null;
  if (ref.type === 'segment' && ref.localA && ref.localB) {
    const a = refLocalPointToWorld(ref.localA, entryRef.transform, ref);
    const b = refLocalPointToWorld(ref.localB, entryRef.transform, ref);
    return {
      kind: 'segment',
      a,
      b,
      ref: {
        ...ref,
        fallbackA: a,
        fallbackB: b,
        baseAxis: normalize(sub(b, a))
      }
    };
  }
  if (ref.localPoint) {
    const point = refLocalPointToWorld(ref.localPoint, entryRef.transform, ref);
    return {
      kind: 'point',
      point,
      ref: {
        ...ref,
        fallbackPoint: point,
        baseAxis: localAxisToWorld({ x: 1, z: 0 }, entryRef.transform)
      }
    };
  }
  return null;
}

function currentRenderedReferenceCache(projectState, modelCache) {
  const renderedCache = snapCandidateCaches.get('rendered-svg');
  // The model cache was refreshed by the caller. An older SVG cache must not
  // resurrect removed geometry; the DOM may still await its next render.
  return renderedCache?.project === projectState && renderedCache.geometryKey === modelCache?.geometryKey
    ? renderedCache
    : null;
}

/** Detached model-only reference view for renderer-independent annotation batches.
 * Rebuild after project/model/geometry changes. Never consults rendered SVG caches.
 * Historical SVG aliases and local spaces use the same compatibility rules.
 */
export function createModelDimensionReferenceResolver(projectState = S.project) {
  const batch = createDimensionSourceBatch();
  collectDimensionSnapCandidates(projectState, { readOnly: true }, batch);
  const cache = snapCandidateCaches.get('model-only');
  const entries = currentDimensionReferenceEntries(projectState, batch);
  function resolve(ref) {
    const normalized = normalizeGeometryRef(ref);
    if (!normalized) return null;
    const local = (normalized.localPoint || normalized.localA && normalized.localB)
      ? resolveGeometryRefFromLocal(normalized, projectState, entries) : null;
    if (local) return { ...local, resolution: 'model-local' };
    const match = unambiguousCandidateMatch(indexedCandidatesForRef(normalized, cache), normalized);
    if (match?.kind === 'segment') return { kind: 'segment', a: vec(match.a), b: vec(match.b),
      ref: Number.isFinite(Number(normalized.t)) ? { ...match.ref, t: normalized.t } : match.ref, resolution: 'model' };
    if (match?.kind === 'point') return { kind: 'point', point: vec(match.point), ref: match.ref, resolution: 'model' };
    const resolution = normalized.entityId ? 'fallback' : 'free';
    return normalized.type === 'segment'
      ? { kind: 'segment', a: vec(normalized.fallbackA), b: vec(normalized.fallbackB), ref: normalized, resolution }
      : { kind: 'point', point: vec(normalized.fallbackPoint), ref: normalized, resolution };
  }
  return Object.freeze({ resolve,
    candidates: () => structuredClone(cache.candidates),
    query: bounds => structuredClone(queryCachedDimensionCandidates(cache, bounds))
  });
}

export function resolveGeometryRef(ref, projectState = S.project) {
  const normalized = normalizeGeometryRef(ref);
  if (!normalized) return null;
  if (normalized.localPoint || (normalized.localA && normalized.localB)) {
    const localResolved = resolveGeometryRefFromLocal(normalized, projectState);
    if (localResolved) return localResolved;
  }
  collectDimensionSnapCandidates(projectState);
  const modelCache = snapCandidateCaches.get('model-only');
  let candidates = indexedCandidatesForRef(normalized, modelCache);
  let match = unambiguousCandidateMatch(candidates, normalized);
  if (!match) {
    const renderedCache = currentRenderedReferenceCache(projectState, modelCache);
    if (renderedCache) {
      candidates = indexedCandidatesForRef(normalized, renderedCache);
      match = unambiguousCandidateMatch(candidates, normalized);
    }
  }
  if (match?.kind === 'segment') return {
    kind: 'segment',
    a: vec(match.a),
    b: vec(match.b),
    ref: Number.isFinite(Number(normalized.t)) ? { ...match.ref, t: normalized.t } : match.ref
  };
  if (match?.kind === 'point') return { kind: 'point', point: vec(match.point), ref: match.ref };
  if (normalized.type === 'segment') return { kind: 'segment', a: vec(normalized.fallbackA), b: vec(normalized.fallbackB), ref: normalized };
  return { kind: 'point', point: vec(normalized.fallbackPoint), ref: normalized };
}

export function resolveDimensionPoints(dimension, resolve = resolveGeometryRef) {
  const refA = resolve(dimension.refA);
  const refB = resolve(dimension.refB);
  const refBaseAxis = refA?.ref?.baseAxis ? normalize(refA.ref.baseAxis) : (refB?.ref?.baseAxis ? normalize(refB.ref.baseAxis) : null);
  if (refA?.kind === 'segment' && refB?.kind === 'segment') {
    const anchorA = segmentAnchorParameter(dimension.refA, dimension.pointA);
    const anchorB = segmentAnchorParameter(dimension.refB, dimension.pointB);
    const hasAnchorA = Number.isFinite(anchorA);
    const hasAnchorB = Number.isFinite(anchorB);
    if (hasAnchorA || hasAnchorB) {
      const pointA = hasAnchorA ? pointOnSegmentAt(refA.a, refA.b, anchorA) : closestPointOnSegment(dimension.pointA, refA.a, refA.b);
      const pointB = hasAnchorB ? pointOnSegmentAt(refB.a, refB.b, anchorB) : closestPointOnSegment(pointA, refB.a, refB.b);
      return {
        pointA,
        pointB,
        segmentA: refA,
        segmentB: refB,
        baseAxis: refBaseAxis || normalize(sub(refA.b, refA.a))
      };
    }
    if (sameSegment(refA, refB)) {
      return {
        pointA: refA.a,
        pointB: refA.b,
        segmentA: refA,
        segmentB: refB,
        baseAxis: refBaseAxis || normalize(sub(refA.b, refA.a))
      };
    }
    const midA = add(refA.a, mul(sub(refA.b, refA.a), 0.5));
    const pointB = closestPointOnSegment(midA, refB.a, refB.b);
    return { pointA: midA, pointB, segmentA: refA, segmentB: refB, baseAxis: refBaseAxis || normalize(sub(refA.b, refA.a)) };
  }
  if (refA?.kind === 'segment' && !dimension.refB) {
    return { pointA: refA.a, pointB: refA.b, segmentA: refA, baseAxis: refBaseAxis || normalize(sub(refA.b, refA.a)) };
  }
  return {
    pointA: refA?.kind === 'point' ? refA.point : vec(dimension.pointA),
    pointB: refB?.kind === 'point' ? refB.point : vec(dimension.pointB),
    segmentA: refA?.kind === 'segment' ? refA : null,
    segmentB: refB?.kind === 'segment' ? refB : null,
    baseAxis: refBaseAxis || (refA?.kind === 'segment' ? normalize(sub(refA.b, refA.a)) : null)
  };
}

function pointerWorld(event) {
  const svg = getPlanRenderTarget();
  const rect = svg?.getBoundingClientRect?.();
  if (!rect) return null;
  const scale = numeric(S.camera2D?.scale, 1) || 1;
  return {
    x: (event.clientX - rect.left - rect.width / 2) / scale + numeric(S.camera2D?.x),
    z: (event.clientY - rect.top - rect.height / 2) / scale + numeric(S.camera2D?.z)
  };
}

function snapToleranceWorld() {
  const scale = Math.max(0.001, numeric(S.camera2D?.scale, 1));
  return SNAP_TOLERANCE_PX / scale;
}

function dimensionAlignmentSnapToleranceWorld() {
  const scale = Math.max(0.001, numeric(S.camera2D?.scale, 1));
  return DIMENSION_ALIGNMENT_SNAP_TOLERANCE_PX / scale;
}

function bestSnapCandidate(point, options = {}) {
  const tolerance = numeric(options.tolerance, snapToleranceWorld());
  let bestPoint = null;
  let bestSegment = null;
  const candidates = queryDimensionSnapCandidates({
    minX: numeric(point?.x) - tolerance,
    maxX: numeric(point?.x) + tolerance,
    minZ: numeric(point?.z ?? point?.y) - tolerance,
    maxZ: numeric(point?.z ?? point?.y) + tolerance
  }, S.project, { includeRenderedSvg: true });
  for (const candidate of candidates) {
    if (candidate.kind === 'point') {
      const score = distance(point, candidate.point);
      if (score <= tolerance && (!bestPoint || score < bestPoint.score)) bestPoint = { candidate, score };
    } else if (candidate.kind === 'segment') {
      const score = distancePointToSegment(point, candidate.a, candidate.b);
      if (score <= tolerance && (!bestSegment || score < bestSegment.score)) bestSegment = { candidate, score };
    }
  }
  if (options.edgesOnly === true) return bestSegment?.candidate || null;
  if (options.preferEdge === true && bestSegment) {
    const scale = Math.max(0.001, numeric(S.camera2D?.scale, 1));
    const pointPriorityTolerance = numeric(options.pointPriorityTolerance, 5 / scale);
    if (!bestPoint || bestPoint.score > pointPriorityTolerance) return bestSegment.candidate;
  }
  return bestPoint?.candidate || bestSegment?.candidate || null;
}

function freePointRef(point) {
  return { type: 'free-point', fallbackPoint: vec(point), role: SNAP_ROLE.FREE };
}

function snappedPoint(point, options = {}) {
  return snappedPointFromCandidate(point, bestSnapCandidate(point, options));
}

// Candidate selection can be shared within one event; each consumer still
// projects its own anchor and receives a separate point / segment reference.
function snappedPointFromCandidate(point, candidate) {
  if (candidate?.kind === 'point') return { point: vec(candidate.point), ref: candidate.ref, candidate };
  if (candidate?.kind === 'segment') {
    const projected = closestPointOnSegment(point, candidate.a, candidate.b);
    return {
      point: projected,
      ref: { ...candidate.ref, t: segmentParameter(projected, candidate.a, candidate.b) },
      candidate
    };
  }
  return { point: vec(point), ref: freePointRef(point), candidate: null };
}

// Öffentliche, neutrale Fangpunktschnittstelle für weitere Core-Werkzeuge.
// Sie nutzt bewusst exakt dieselbe Kandidatenliste und Kantenprojektion wie
// die lineare Bemaßung, damit ein Punkt in beiden Werkzeugen identisch
// erkannt und mit derselben stabilen Geometriereferenz gespeichert wird.
export function snapToDimensionGeometry(point, options = {}) {
  const snapped = snappedPoint(point, options);
  return {
    point: snapped.point,
    ref: snapped.ref,
    candidate: snapped.candidate,
    role: snapped.candidate?.role || snapped.ref?.role || SNAP_ROLE.FREE
  };
}

function dimensionHoverSignature(hover) {
  if (!hover?.point) return '';
  const ref = hover.candidate?.ref || {};
  return [
    hover.role || '',
    ref.entityId || '',
    ref.type || '',
    ref.index ?? ref.corner ?? '',
    Math.round(numeric(hover.point.x) * 2) / 2,
    Math.round(numeric(hover.point.z) * 2) / 2
  ].join('|');
}

function setDimensionHover(hover) {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  const previousSignature = dimensionHoverSignature(S.ui.dimensionHover);
  const nextSignature = dimensionHoverSignature(hover);
  if (previousSignature === nextSignature) return false;
  S.ui.dimensionHover = hover || null;
  return true;
}

function dimensionSnapInfo(point, candidate = bestSnapCandidate(point)) {
  const snapped = snappedPointFromCandidate(point, candidate);
  return {
    point: snapped.point,
    candidate: snapped.candidate,
    role: snapped.candidate?.role || snapped.ref?.role || SNAP_ROLE.FREE
  };
}

function offsetFromPointer(pointA, pointB, mode, pointer, baseAxis = null) {
  const axis = axisForDimension({ mode }, pointA, pointB, { baseAxis });
  const normal = normalForDimensionAxis(axis, mode);
  return dot(sub(pointer, pointA), normal);
}

function clearDimensionAlignmentSnap() {
  if (S.ui?.dimensionAlignmentSnap) S.ui.dimensionAlignmentSnap = null;
}

function setDimensionAlignmentSnap(snap) {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.dimensionAlignmentSnap = snap || null;
}

function dimensionAlignmentSignature(snap) {
  return snap?.dimensionId || '';
}

function dimensionAlignmentChanged(previous, next) {
  return dimensionAlignmentSignature(previous) !== dimensionAlignmentSignature(next);
}

function dimensionsHaveSameAlignment(candidateLayout, targetLayout, candidateMode, targetMode) {
  if (!candidateLayout || !targetLayout) return false;
  // Mode names describe how endpoints are projected, not the rendered line's
  // direction. Aligned and horizontal/vertical dimensions may share a line.
  if (candidateMode === 'angular' || targetMode === 'angular') return false;
  return Math.abs(dot(normalize(candidateLayout.axis), normalize(targetLayout.axis))) > Math.cos(PARALLEL_TOLERANCE_RAD);
}

function dimensionAlignmentSnap(pointA, pointB, mode, rawOffset, baseAxis = null, options = {}) {
  const currentDimensionId = String(options.dimensionId || '').trim();
  const draftLayout = dimensionLayout({ mode, pointA, pointB, offset: rawOffset }, pointA, pointB, { baseAxis });
  const tolerance = dimensionAlignmentSnapToleranceWorld();
  let best = null;
  for (const target of ensureDimensionState()) {
    if (!target?.id || target.id === currentDimensionId) continue;
    const targetResolved = resolveDimensionPoints(target);
    const targetLayout = dimensionLayout(target, targetResolved.pointA, targetResolved.pointB, targetResolved);
    if (!dimensionsHaveSameAlignment(draftLayout, targetLayout, mode, target.mode)) continue;
    const signedDistance = dot(sub(targetLayout.dimA, draftLayout.dimA), draftLayout.normal);
    const score = Math.abs(signedDistance);
    if (score > tolerance) continue;
    if (!best || score < best.score) {
      best = {
        dimensionId: target.id,
        offset: rawOffset + signedDistance,
        score
      };
    }
  }
  return best;
}

function offsetWithDimensionAlignmentSnap(pointA, pointB, mode, rawOffset, baseAxis = null, options = {}) {
  const snap = dimensionAlignmentSnap(pointA, pointB, mode, rawOffset, baseAxis, options);
  setDimensionAlignmentSnap(snap);
  return snap ? snap.offset : rawOffset;
}

function draftState() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  if (!S.ui.dimensionDraft || typeof S.ui.dimensionDraft !== 'object') S.ui.dimensionDraft = null;
  return S.ui.dimensionDraft;
}

function setDraft(draft) {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.dimensionDraft = draft;
  if (!draft) {
    setDimensionHover(null);
    clearDimensionAlignmentSnap();
  }
}

export function isDimensionToolActive() {
  return S.ui?.activeTool?.owner === 'core'
    && [DIMENSION_TOOL, ANGULAR_DIMENSION_TOOL].includes(S.ui.activeTool.tool);
}

export function isAngularDimensionToolActive() {
  return S.ui?.activeTool?.owner === 'core' && S.ui.activeTool.tool === ANGULAR_DIMENSION_TOOL;
}

export function activateAngularDimensionTool() {
  activateDimensionTool();
  S.ui.activeTool = { owner: 'core', tool: ANGULAR_DIMENSION_TOOL, meta: {} };
  setStatusText('Winkelbemassung · erste Kante anklicken');
  syncDimensionToolbarButton();
  return true;
}

export function activateDimensionTool() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.activeTool = { owner: 'core', tool: DIMENSION_TOOL, meta: {} };
  setDraft(null);
  setStatusText('Bemassung · ersten Punkt oder erste Kante anklicken');
  syncDimensionToolbarButton();
  return true;
}

export function deactivateDimensionTool() {
  if (isDimensionToolActive()) {
    S.ui.activeTool = null;
  }
  setDraft(null);
  syncDimensionToolbarButton();
  setStatusText('Bemassung deaktiviert');
  AppRuntime.requestPlanRender('dimension-tool-off');
  return true;
}

export function cancelDimensionInteraction() {
  const hadDraft = Boolean(S.ui?.dimensionDraft);
  if (hadDraft) {
    setDraft(null);
    AppRuntime.requestPlanRender('dimension-cancel', { kind: 'annotations' });
    setStatusText('Bemassung abgebrochen');
    return true;
  }
  if (isDimensionToolActive()) {
    S.ui.activeTool = null;
    if (S.selection?.dimensionId) S.selection.dimensionId = null;
    syncDimensionToolbarButton();
    setStatusText('Bemassung deaktiviert');
    return true;
  }
  if (S.selection?.dimensionId) {
    S.selection.dimensionId = null;
    setStatusText('Auswahl aufgehoben');
    return true;
  }
  return false;
}

function setStatusText(text) {
  const el = document.getElementById('statusText') || document.getElementById('statusbar');
  if (el) el.textContent = text;
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.statusText = text;
}

export function syncDimensionToolbarButton() {
  for (const [id, tool] of [['btnDimensionTool', DIMENSION_TOOL], ['btnAngularDimensionTool', ANGULAR_DIMENSION_TOOL]]) {
    const button = document.getElementById(id);
    if (!button) continue;
    const active = isDimensionToolActive() && S.ui.activeTool.tool === tool;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
}

export function selectedDimensionId() {
  return S.selection?.dimensionId || null;
}

export function selectDimension(id) {
  ensureDimensionState();
  if (!S.project.dimensions.some(dimension => dimension.id === id)) return false;
  S.selection.dimensionId = id;
  S.selection.areaMeasurementId = null;
  S.selection.textAnnotationId = null;
  AppRuntime.clearMultiSelection();
  setStatusText('Masslinie markiert');
  return true;
}

export function clearDimensionSelection() {
  if (!S.selection?.dimensionId) return false;
  S.selection.dimensionId = null;
  return true;
}

export function deleteSelectedDimension() {
  const id = selectedDimensionId();
  if (!id) return false;
  const dimensions = ensureDimensionState();
  const index = dimensions.findIndex(dimension => dimension.id === id);
  if (index < 0) return false;
  recordUndo('Bemassung löschen');
  dimensions.splice(index, 1);
  S.selection.dimensionId = null;
  if (S.file) S.file.dirty = true;
  setStatusText('Bemassung gelöscht');
  AppRuntime.requestAppRefresh(false, 'dimension-delete');
  return true;
}

function createDimension(data) {
  const dimensions = ensureDimensionState();
  const dimension = normalizeDimensionEntity({
    mode: 'aligned',
    offset: 120,
    unit: DEFAULT_UNIT,
    precision: DEFAULT_PRECISION,
    ...data,
    createdAt: Date.now(),
    updatedAt: Date.now()
  });
  recordUndo('Bemassung setzen');
  dimensions.push(dimension);
  S.selection.dimensionId = dimension.id;
  if (S.file) S.file.dirty = true;
  setStatusText('Bemassung gesetzt');
  AppRuntime.requestAppRefresh(false, 'dimension-create');
  return dimension;
}

function modeFromModifier(event, pointA, pointB) {
  if (!event?.shiftKey) return null;
  const delta = sub(pointB, pointA);
  return Math.abs(delta.x) >= Math.abs(delta.z) ? 'horizontal' : 'vertical';
}

function modeFromPlacementPointer(pointA, pointB, pointer, event = null, baseAxis = null) {
  const modifierMode = modeFromModifier(event, pointA, pointB);
  if (modifierMode) return modifierMode;
  const center = add(pointA, mul(sub(pointB, pointA), 0.5));
  const drag = sub(pointer, center);
  if (length(drag) < MIN_VECTOR_LENGTH) return 'aligned';
  const dragDirection = normalize(drag);
  const alignedNormal = normalize(perpendicular(normalize(sub(pointB, pointA))));
  const referenceAxis = baseAxis ? normalize(baseAxis) : { x: 1, z: 0 };
  const referenceNormal = normalize(perpendicular(referenceAxis));
  const scores = [
    ['aligned', Math.abs(dot(dragDirection, alignedNormal))],
    ['horizontal', Math.abs(dot(dragDirection, referenceNormal))],
    ['vertical', Math.abs(dot(dragDirection, referenceAxis))]
  ];
  scores.sort((a, b) => b[1] - a[1]);
  return scores[0][0];
}

function dimensionIsFree(dimension) {
  const refA = normalizeGeometryRef(dimension?.refA);
  const refB = normalizeGeometryRef(dimension?.refB);
  if (refA?.type === 'segment' && !refB) return false;
  const isFreeRef = ref => !ref || (ref.type === 'free-point' && !ref.entityId);
  return isFreeRef(refA) || isFreeRef(refB);
}

function geometryRefHasStableTarget(ref, projectState = S.project) {
  const normalized = normalizeGeometryRef(ref);
  if (!normalized || (normalized.type === 'free-point' && !normalized.entityId)) return false;
  if ((normalized.localPoint || (normalized.localA && normalized.localB))
    && bestReferenceEntryForRef(normalized, projectState)?.transform) {
    return true;
  }
  collectDimensionSnapCandidates(projectState);
  const modelCache = snapCandidateCaches.get('model-only');
  if (indexedCandidatesForRef(normalized, modelCache)
    .some(candidate => candidateMatchesRef(candidate, normalized))) return true;
  const renderedCache = currentRenderedReferenceCache(projectState, modelCache);
  return Boolean(renderedCache)
    && indexedCandidatesForRef(normalized, renderedCache)
      .some(candidate => candidateMatchesRef(candidate, normalized));
}

function dimensionHasUnstableRef(dimension, projectState = S.project) {
  const refs = [normalizeGeometryRef(dimension?.refA), normalizeGeometryRef(dimension?.refB)];
  return refs.some(ref => {
    if (!ref || (ref.type === 'free-point' && !ref.entityId)) return false;
    return !geometryRefHasStableTarget(ref, projectState);
  });
}

function dimensionStatusClass(dimension) {
  if (dimensionIsFree(dimension)) return 'is-free';
  if (dimensionHasUnstableRef(dimension)) return 'is-unstable';
  return 'is-anchored';
}

function sameSegment(a, b) {
  return a?.ref?.entityId && b?.ref?.entityId
    && a.ref.entityId === b.ref.entityId
    && a.ref.type === 'segment'
    && b.ref.type === 'segment'
    && Number(a.ref.index) === Number(b.ref.index);
}

function segmentAnchorParameter(ref = {}, fallbackPoint = null) {
  const explicit = Number(ref.t ?? ref.anchorT);
  if (Number.isFinite(explicit)) return clamp01(explicit);
  if (validPoint(ref.fallbackA) && validPoint(ref.fallbackB) && validPoint(fallbackPoint)) {
    return segmentParameter(vec(fallbackPoint), vec(ref.fallbackA), vec(ref.fallbackB));
  }
  return null;
}

function snappedSecondPoint(point, draft = null, candidate = bestSnapCandidate(point)) {
  const anchor = candidate?.kind === 'segment' && draft?.firstSegment && !sameSegment(draft.firstSegment, candidate)
    ? vec(draft.pointA)
    : point;
  return snappedPointFromCandidate(anchor, candidate);
}

function targetFromFirstSegmentDraft(candidate, draft = {}) {
  const segment = draft.firstSegment;
  if (!segment || !candidate) return null;
  if (candidate.kind === 'point') {
    const pointB = vec(candidate.point);
    const pointA = closestPointOnSegment(pointB, segment.a, segment.b);
    return {
      pointA,
      pointB,
      refA: { ...segment.ref, t: segmentParameter(pointA, segment.a, segment.b) },
      refB: candidate.ref,
      target: candidate
    };
  }
  if (candidate.kind === 'segment' && !sameSegment(segment, candidate)) {
    const pointA = draft.anchorPoint || add(segment.a, mul(sub(segment.b, segment.a), 0.5));
    const pointB = closestPointOnSegment(pointA, candidate.a, candidate.b);
    return {
      pointA,
      pointB,
      refA: { ...segment.ref, t: segmentParameter(pointA, segment.a, segment.b) },
      refB: { ...candidate.ref, t: segmentParameter(pointB, candidate.a, candidate.b) },
      target: candidate
    };
  }
  return null;
}

function angularDraftDimension(draft, candidate, world) {
  if (!draft?.firstSegment || !candidate) return null;
  const first = draft.firstSegment;
  if ((distance(first.a, candidate.a) < MIN_VECTOR_LENGTH && distance(first.b, candidate.b) < MIN_VECTOR_LENGTH)
    || (distance(first.a, candidate.b) < MIN_VECTOR_LENGTH && distance(first.b, candidate.a) < MIN_VECTOR_LENGTH)) return null;
  const pointB = closestPointOnSegment(world, candidate.a, candidate.b);
  const dimension = normalizeDimensionEntity({
    id: 'dimension-preview', mode: 'angular', pointA: draft.pointA, pointB,
    refA: draft.refA,
    refB: { ...candidate.ref, t: segmentParameter(pointB, candidate.a, candidate.b) },
    offset: 65 / Math.max(0.001, numeric(S.camera2D?.scale, 1)), precision: 1, unit: '°'
  });
  return angularDimensionLayout(dimension) ? dimension : null;
}

function placeAngularDimension(world) {
  const placement = draftState();
  if (placement?.phase === 'angular-placement') {
    const dimension = placement.angularPreview;
    Object.assign(dimension, angularDimensionPlacement(angularDimensionLayout(dimension), world));
    createDimension({ ...dimension, id: makeId('dimension') });
    setDraft(null);
    setStatusText('Winkelbemassung gesetzt · Beschriftung ziehen, um Seite und Abstand zu ändern');
    AppRuntime.requestPlanRender('angular-dimension-place', { kind: 'annotations' });
    return true;
  }
  const candidate = bestSnapCandidate(world, { edgesOnly: true });
  if (!candidate) {
    setStatusText('Winkelbemassung · bitte eine gerade Kante anklicken');
    return true;
  }
  const draft = draftState();
  if (!draft) {
    const pointA = closestPointOnSegment(world, candidate.a, candidate.b);
    setDraft({ phase: 'angular-second-edge', firstSegment: candidate, pointA,
      refA: { ...candidate.ref, t: segmentParameter(pointA, candidate.a, candidate.b) } });
    setStatusText('Winkelbemassung · zweite Kante anklicken');
  } else {
    const dimension = angularDraftDimension(draft, candidate, world);
    if (!dimension) {
      setStatusText('Winkelbemassung · eine andere, gültige Kante wählen');
      return true;
    }
    setDraft({ phase: 'angular-placement', angularPreview: dimension });
    setStatusText('Winkelbemassung · Winkelseite und Abstand wählen, zum Platzieren klicken');
  }
  AppRuntime.requestPlanRender('angular-dimension-preview', { kind: 'annotations' });
  return true;
}

function placeDimensionFromDraft(event, world) {
  const draft = draftState();
  if (!draft) {
    const candidate = bestSnapCandidate(world);
    if (candidate?.kind === 'segment') {
      const anchorPoint = closestPointOnSegment(world, candidate.a, candidate.b);
      const baseAxis = normalize(sub(candidate.b, candidate.a));
      setDraft({
        phase: 'edge-or-target',
        pointA: vec(candidate.a),
        pointB: vec(candidate.b),
        refA: { ...candidate.ref, t: 0 },
        refB: { ...candidate.ref, t: 1 },
        anchorPoint,
        firstSegment: candidate,
        baseAxis,
        mode: 'aligned',
        offset: 120
      });
      setStatusText('Bemassung · zweite Kante/Punkt wählen oder Kantenmass platzieren');
      return true;
    }
    const snapped = snappedPointFromCandidate(world, candidate);
    setDraft({
      phase: 'second-point',
      pointA: snapped.point,
      refA: snapped.ref,
      baseAxis: snapped.candidate?.kind === 'segment' ? normalize(sub(snapped.candidate.b, snapped.candidate.a)) : null
    });
    setStatusText('Bemassung · zweiten Punkt anklicken');
    return true;
  }

  if (draft.phase === 'edge-or-target') {
    const candidate = bestSnapCandidate(world);
    const target = targetFromFirstSegmentDraft(candidate, draft);
    if (target) {
      setDraft({
        phase: 'offset',
        pointA: target.pointA,
        pointB: target.pointB,
        refA: target.refA,
        refB: target.refB,
        baseAxis: draft.baseAxis,
        mode: 'aligned',
        offset: 120
      });
      setStatusText('Bemassung · Masslinie ziehen und mit drittem Klick platzieren');
      return true;
    }
    const mode = modeFromPlacementPointer(draft.pointA, draft.pointB, world, event, draft.baseAxis);
    const rawOffset = offsetFromPointer(draft.pointA, draft.pointB, mode, world, draft.baseAxis);
    const offset = offsetWithDimensionAlignmentSnap(draft.pointA, draft.pointB, mode, rawOffset, draft.baseAxis);
    createDimension({
      mode,
      pointA: draft.pointA,
      pointB: draft.pointB,
      refA: draft.refA,
      refB: draft.refB,
      offset
    });
    setDraft(null);
    return true;
  }

  if (draft.phase === 'second-point') {
    const snapped = snappedSecondPoint(world, draft);
    const baseAxis = draft.baseAxis || (snapped.candidate?.kind === 'segment' ? normalize(sub(snapped.candidate.b, snapped.candidate.a)) : null);
    setDraft({
      phase: 'offset',
      pointA: draft.pointA,
      pointB: snapped.point,
      refA: draft.refA,
      refB: snapped.ref,
      baseAxis,
      mode: 'aligned',
      offset: 120
    });
    setStatusText('Bemassung · Masslinie ziehen und mit drittem Klick platzieren');
    return true;
  }

  if (draft.phase === 'offset') {
    const mode = modeFromPlacementPointer(draft.pointA, draft.pointB, world, event, draft.baseAxis);
    const rawOffset = offsetFromPointer(draft.pointA, draft.pointB, mode, world, draft.baseAxis);
    const offset = offsetWithDimensionAlignmentSnap(draft.pointA, draft.pointB, mode, rawOffset, draft.baseAxis);
    createDimension({
      mode,
      pointA: draft.pointA,
      pointB: draft.pointB,
      refA: draft.refA,
      refB: draft.refB,
      offset
    });
    setDraft(null);
    return true;
  }
  return false;
}

function startHandleDrag(event, handle, dimensionId) {
  const dimension = ensureDimensionState().find(entry => entry.id === dimensionId);
  if (!dimension) return false;
  recordUndo('Bemassung bearbeiten');
  const world = pointerWorld(event);
  if (!world) return false;
  S.drag = {
    ...(S.drag || {}),
    active: true,
    type: 'dimension',
    dimensionId,
    handle,
    startMouse: world,
    startDimension: JSON.parse(JSON.stringify(dimension))
  };
  S.selection.dimensionId = dimensionId;
  event.preventDefault();
  event.stopPropagation?.();
  return true;
}

function dimensionFromEventTarget(target) {
  const dimensionNode = target?.closest?.('[data-dimension-id]');
  const id = dimensionNode?.dataset?.dimensionId;
  return id ? ensureDimensionState().find(dimension => dimension.id === id) : null;
}

export function handleDimensionPointerDown(event) {
  if (S.project?.currentView !== 'plan') return false;
  if (event.button !== 0) return false;
  const handleNode = event.target?.closest?.('[data-dimension-handle]');
  if (handleNode?.dataset?.dimensionId && handleNode.dataset.dimensionHandle) {
    return startHandleDrag(event, handleNode.dataset.dimensionHandle, handleNode.dataset.dimensionId);
  }
  const hitDimension = dimensionFromEventTarget(event.target);
  if (hitDimension) {
    selectDimension(hitDimension.id);
    event.preventDefault();
    event.stopPropagation?.();
    AppRuntime.requestPlanRender('dimension-select');
    return true;
  }
  if (!isDimensionToolActive()) return false;
  const world = pointerWorld(event);
  if (!world) return false;
  event.preventDefault();
  event.stopPropagation?.();
  return isAngularDimensionToolActive()
    ? placeAngularDimension(world)
    : placeDimensionFromDraft(event, world);
}

export function handleDimensionPointerMove(event) {
  if (S.project?.currentView !== 'plan') return false;
  const world = pointerWorld(event);
  if (!world) return false;
  if (S.drag?.active && S.drag.type === 'dimension') {
    const dimension = ensureDimensionState().find(entry => entry.id === S.drag.dimensionId);
    if (!dimension) return false;
    const handle = S.drag.handle;
    if (dimension.mode === 'angular') {
      const layout = dimensionLayout(dimension, null, null, resolveDimensionPoints(dimension));
      if (!layout) return false;
      Object.assign(dimension, angularDimensionPlacement(layout, world));
      dimension.updatedAt = Date.now();
    } else if (handle === 'a' || handle === 'b') {
      clearDimensionAlignmentSnap();
      const snapped = snappedPoint(world);
      if (handle === 'a') {
        dimension.pointA = snapped.point;
        dimension.refA = snapped.ref;
      } else {
        dimension.pointB = snapped.point;
        dimension.refB = snapped.ref;
      }
      dimension.updatedAt = Date.now();
    } else if (handle === 'offset' || handle === 'text') {
      const resolved = resolveDimensionPoints(dimension);
      dimension.mode = modeFromPlacementPointer(resolved.pointA, resolved.pointB, world, event, resolved.baseAxis);
      const rawOffset = offsetFromPointer(resolved.pointA, resolved.pointB, dimension.mode, world, resolved.baseAxis);
      dimension.offset = offsetWithDimensionAlignmentSnap(resolved.pointA, resolved.pointB, dimension.mode, rawOffset, resolved.baseAxis, { dimensionId: dimension.id });
      dimension.textPosition = null;
      dimension.updatedAt = Date.now();
    }
    if (S.file) S.file.dirty = true;
    AppRuntime.requestPlanRender('dimension-drag', { kind: 'annotations' });
    return true;
  }
  const draft = draftState();
  if (isAngularDimensionToolActive()) {
    if (draft?.phase === 'angular-placement') {
      setDimensionHover(null);
      Object.assign(draft.angularPreview, angularDimensionPlacement(angularDimensionLayout(draft.angularPreview), world));
      AppRuntime.requestPlanRender('angular-dimension-preview', { kind: 'annotations' });
      return true;
    }
    const candidate = bestSnapCandidate(world, { edgesOnly: true });
    setDimensionHover(candidate ? dimensionSnapInfo(world, candidate) : null);
    if (draft) draft.angularPreview = angularDraftDimension(draft, candidate, world);
    AppRuntime.requestPlanRender('angular-dimension-preview', { kind: 'annotations' });
    return true;
  }
  if (!draft && !isDimensionToolActive()) return false;
  let needsRender = false;
  if (draft?.phase === 'offset') {
    const previousMode = draft.mode;
    const previousOffset = numeric(draft.offset);
    const previousSnap = S.ui?.dimensionAlignmentSnap || null;
    draft.mode = modeFromPlacementPointer(draft.pointA, draft.pointB, world, event, draft.baseAxis);
    const rawOffset = offsetFromPointer(draft.pointA, draft.pointB, draft.mode, world, draft.baseAxis);
    draft.offset = offsetWithDimensionAlignmentSnap(draft.pointA, draft.pointB, draft.mode, rawOffset, draft.baseAxis);
    needsRender = previousMode !== draft.mode || Math.abs(previousOffset - numeric(draft.offset)) > 0.5;
    needsRender = dimensionAlignmentChanged(previousSnap, S.ui?.dimensionAlignmentSnap) || needsRender;
  } else if (draft?.phase === 'edge-or-target') {
    const previousMode = draft.mode;
    const previousOffset = numeric(draft.offset);
    const previousPreviewA = draft.previewPointA;
    const previousPreviewB = draft.previewPointB;
    const previousSnap = S.ui?.dimensionAlignmentSnap || null;
    const candidate = bestSnapCandidate(world);
    const target = targetFromFirstSegmentDraft(candidate, draft);
    if (target) {
      clearDimensionAlignmentSnap();
      draft.previewPointA = target.pointA;
      draft.previewPointB = target.pointB;
      draft.previewRefA = target.refA;
      draft.previewRefB = target.refB;
    } else {
      draft.previewPointA = null;
      draft.previewPointB = null;
      draft.previewRefA = null;
      draft.previewRefB = null;
      draft.mode = modeFromPlacementPointer(draft.pointA, draft.pointB, world, event, draft.baseAxis);
      const rawOffset = offsetFromPointer(draft.pointA, draft.pointB, draft.mode, world, draft.baseAxis);
      draft.offset = offsetWithDimensionAlignmentSnap(draft.pointA, draft.pointB, draft.mode, rawOffset, draft.baseAxis);
    }
    needsRender ||= previousMode !== draft.mode
      || Math.abs(previousOffset - numeric(draft.offset)) > 0.5
      || distance(previousPreviewA, draft.previewPointA) > 0.5
      || distance(previousPreviewB, draft.previewPointB) > 0.5
      || dimensionAlignmentChanged(previousSnap, S.ui?.dimensionAlignmentSnap);
    needsRender = setDimensionHover(dimensionSnapInfo(world, candidate)) || needsRender;
  } else if (draft?.phase === 'second-point') {
    const previousSnap = S.ui?.dimensionAlignmentSnap || null;
    clearDimensionAlignmentSnap();
    const candidate = bestSnapCandidate(world);
    const snapped = snappedSecondPoint(world, draft, candidate);
    needsRender = !draft.previewPointB || distance(draft.previewPointB, snapped.point) > 0.5;
    draft.previewPointB = snapped.point;
    draft.previewRefB = snapped.ref;
    needsRender = dimensionAlignmentChanged(previousSnap, S.ui?.dimensionAlignmentSnap) || needsRender;
    needsRender = setDimensionHover(dimensionSnapInfo(world, candidate)) || needsRender;
  } else if (!draft && isDimensionToolActive()) {
    const previousSnap = S.ui?.dimensionAlignmentSnap || null;
    clearDimensionAlignmentSnap();
    needsRender = dimensionAlignmentChanged(previousSnap, S.ui?.dimensionAlignmentSnap) || needsRender;
    needsRender = setDimensionHover(dimensionSnapInfo(world)) || needsRender;
  }
  if (needsRender) AppRuntime.requestPlanRender('dimension-preview', { kind: 'annotations' });
  return true;
}

export function handleDimensionPointerUp() {
  if (S.drag?.active && S.drag.type === 'dimension') {
    S.drag.active = false;
    S.drag.type = null;
    S.drag.dimensionId = null;
    S.drag.handle = null;
    clearDimensionAlignmentSnap();
    setStatusText('Bemassung bearbeitet');
    AppRuntime.requestPlanRender('dimension-drag-finished');
    return true;
  }
  return false;
}

['object:moved', 'object:rotated', 'object:scaled', 'object:pasted', 'object:deleted', 'scene:changed']
  .forEach(type => on(type, invalidateDimensionSnapCandidateCache));

export function wantsDimensionPointerMoveFrame() {
  return isDimensionToolActive() || S.drag?.type === 'dimension' || Boolean(S.ui?.dimensionDraft);
}

function screenPoint(worldPoint, worldToScreen) {
  const p = worldToScreen(worldPoint.x, worldPoint.z);
  return { x: numeric(p.x), y: numeric(p.y) };
}

function svgLine(svgEl, parent, a, b, attrs = {}) {
  return svgEl('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, ...attrs }, parent);
}

function arrowPolygonPoints(point, inwardAxis, lengthValue, widthValue) {
  const a = normalize(inwardAxis);
  const n = normalize(perpendicular(a));
  const tip = point;
  const back = add(point, mul(a, lengthValue));
  const p1 = add(back, mul(n, widthValue / 2));
  const p2 = add(back, mul(n, -widthValue / 2));
  return `${tip.x},${tip.y} ${p1.x},${p1.z} ${p2.x},${p2.z}`;
}

function dimensionPlanLabelMetrics() {
  return resolvePlanLabelMetrics({
    cameraScale: S.camera2D?.scale,
    scaleMode: isPrintPlanCaptureInProgress()
      ? PLAN_LABEL_SCALE_MODES.SCREEN
      : PLAN_LABEL_SCALE_MODES.MODEL,
    screenFontSizePx: DIMENSION_LABEL_REFERENCE_FONT_PX
  });
}

function readableTextAngleDeg(axis) {
  let angle = Math.atan2(numeric(axis?.z ?? axis?.y), numeric(axis?.x)) * 180 / Math.PI;
  while (angle > 90) angle -= 180;
  while (angle <= -90) angle += 180;
  return angle;
}

function renderSingleDimension(parent, dimension, ctx, options = {}) {
  const { svgEl, worldToScreen } = ctx;
  const labelMetrics = ctx.labelMetrics || dimensionPlanLabelMetrics();
  const labelScaleFactor = labelMetrics.fontSizePx / DIMENSION_LABEL_REFERENCE_FONT_PX;
  const arrowLengthPx = DIMENSION_ARROW_REFERENCE_LENGTH_PX * labelScaleFactor;
  const arrowWidthPx = DIMENSION_ARROW_REFERENCE_WIDTH_PX * labelScaleFactor;
  const resolved = resolveDimensionPoints(dimension);
  const layout = dimensionLayout(dimension, resolved.pointA, resolved.pointB, resolved);
  if (dimension.mode === 'angular') return renderAngularDimension(parent, dimension, layout,
    { ...ctx, labelMetrics }, { ...options, statusClass: dimensionStatusClass(dimension),
      label: dimension.textOverride || formatDimensionValue(layout?.value, dimension) });
  const selected = options.selected === true;
  const alignmentTarget = options.alignmentTarget === true;
  const a = screenPoint(layout.pointA, worldToScreen);
  const b = screenPoint(layout.pointB, worldToScreen);
  const da = screenPoint(layout.dimA, worldToScreen);
  const db = screenPoint(layout.dimB, worldToScreen);
  const text = screenPoint(layout.textPosition, worldToScreen);
  const axis = normalize({ x: db.x - da.x, z: db.y - da.y });
  const textNormal = normalize(perpendicular(axis));
  const labelOffsetPx = DIMENSION_LABEL_OFFSET_REFERENCE_PX * labelScaleFactor;
  const labelPoint = add(text, mul(textNormal, -labelOffsetPx));
  const labelAngle = readableTextAngleDeg(axis);
  const valueLabel = dimension.textOverride || formatDimensionValue(layout.value, dimension);
  const statusClass = dimensionStatusClass(dimension);
  const group = svgEl('g', {
    class: `dimension ${statusClass}${selected ? ' is-selected' : ''}${alignmentTarget ? ' is-alignment-target' : ''}${options.preview ? ' is-preview' : ''}`,
    style: `--dimension-visual-scale:${labelScaleFactor}`,
    'data-dimension-id': dimension.id || ''
  }, parent);
  svgLine(svgEl, group, a, da, { class: 'dimension-extension-line' });
  svgLine(svgEl, group, b, db, { class: 'dimension-extension-line' });
  svgLine(svgEl, group, da, db, { class: 'dimension-line' });
  svgLine(svgEl, group, da, db, {
    class: 'dimension-hit-line',
    'data-dimension-id': dimension.id || ''
  });
  svgEl('polygon', {
    class: 'dimension-arrow',
    points: arrowPolygonPoints({ x: da.x, y: da.y }, axis, arrowLengthPx, arrowWidthPx)
  }, group);
  svgEl('polygon', {
    class: 'dimension-arrow',
    points: arrowPolygonPoints({ x: db.x, y: db.y }, mul(axis, -1), arrowLengthPx, arrowWidthPx)
  }, group);
  const label = svgEl('text', {
    class: 'dimension-text',
    x: labelPoint.x,
    y: labelPoint.z,
    'text-anchor': 'middle',
    transform: `rotate(${labelAngle} ${labelPoint.x} ${labelPoint.z})`,
    'data-dimension-id': dimension.id || '',
    'data-plan-label-size': labelMetrics.size,
    'data-plan-label-scale-mode': labelMetrics.scaleMode,
    'font-size': labelMetrics.fontSizePx,
    ...(selected && !options.preview ? { 'data-dimension-handle': 'offset' } : {})
  }, group);
  label.textContent = valueLabel;
  if (selected && !options.preview) {
    [
      ['a', a],
      ['b', b],
      ['offset', { x: (da.x + db.x) / 2, y: (da.y + db.y) / 2 }]
    ].forEach(([handle, point]) => {
      svgEl('circle', {
        class: `dimension-handle dimension-handle-${handle}`,
        cx: point.x,
        cy: point.y,
        r: handle === 'offset' ? 5.5 : 5,
        'data-dimension-id': dimension.id,
        'data-dimension-handle': handle
      }, group);
    });
  }
  return group;
}

function previewDimensionFromDraft() {
  const draft = draftState();
  if (!draft) return null;
  if (draft.phase === 'angular-second-edge' || draft.phase === 'angular-placement') return draft.angularPreview || null;
  if (draft.phase === 'edge-or-target') {
    return normalizeDimensionEntity({
      id: 'dimension-preview',
      mode: draft.previewPointB ? 'aligned' : draft.mode || 'aligned',
      pointA: draft.previewPointA || draft.pointA,
      pointB: draft.previewPointB || draft.pointB,
      refA: draft.previewRefA || draft.refA,
      refB: draft.previewPointB ? draft.previewRefB : draft.refB,
      offset: draft.previewPointB ? 0 : numeric(draft.offset, 120),
      unit: DEFAULT_UNIT,
      precision: DEFAULT_PRECISION
    });
  }
  if (draft.phase === 'second-point' && draft.previewPointB) {
    return normalizeDimensionEntity({
      id: 'dimension-preview',
      mode: modeFromModifier(null, draft.pointA, draft.previewPointB),
      pointA: draft.pointA,
      pointB: draft.previewPointB,
      refA: draft.refA,
      refB: draft.previewRefB,
      offset: 0,
      unit: DEFAULT_UNIT,
      precision: DEFAULT_PRECISION
    });
  }
  if (draft.phase === 'offset') {
    return normalizeDimensionEntity({
      id: 'dimension-preview',
      mode: draft.mode || 'aligned',
      pointA: draft.pointA,
      pointB: draft.pointB,
      refA: draft.refA,
      refB: draft.refB,
      offset: numeric(draft.offset, 120),
      unit: DEFAULT_UNIT,
      precision: DEFAULT_PRECISION
    });
  }
  return null;
}

export function renderDimensionSnapPreview(layer, ctx, hover) {
  if (!hover?.point || typeof ctx.svgEl !== 'function' || typeof ctx.worldToScreen !== 'function') return;
  const role = hover.role || SNAP_ROLE.FREE;
  if (hover.candidate?.kind === 'segment') {
    const a = screenPoint(hover.candidate.a, ctx.worldToScreen);
    const b = screenPoint(hover.candidate.b, ctx.worldToScreen);
    ctx.svgEl('line', {
      class: 'dimension-snap-preview dimension-snap-preview-edge',
      x1: a.x,
      y1: a.y,
      x2: b.x,
      y2: b.y
    }, layer);
  }
  const p = screenPoint(hover.point, ctx.worldToScreen);
  if (role === SNAP_ROLE.MIDPOINT) {
    ctx.svgEl('rect', {
      class: 'dimension-snap-preview dimension-snap-preview-midpoint',
      x: p.x - 4,
      y: p.y - 4,
      width: 8,
      height: 8
    }, layer);
    return;
  }
  if (role === SNAP_ROLE.CENTER) {
    ctx.svgEl('path', {
      class: 'dimension-snap-preview dimension-snap-preview-center',
      d: `M${p.x - 6},${p.y}L${p.x + 6},${p.y}M${p.x},${p.y - 6}L${p.x},${p.y + 6}`
    }, layer);
    return;
  }
  ctx.svgEl('circle', {
    class: `dimension-snap-preview dimension-snap-preview-${role}`,
    cx: p.x,
    cy: p.y,
    r: role === SNAP_ROLE.FREE ? 4 : 4.5
  }, layer);
}

export function renderDimensions2D(parent, ctx = {}) {
  if (!parent || typeof ctx.svgEl !== 'function' || typeof ctx.worldToScreen !== 'function') return;
  const layer = ctx.svgEl('g', { class: 'dimension-layer' }, parent);
  const renderCtx = { ...ctx, labelMetrics: ctx.labelMetrics || dimensionPlanLabelMetrics() };
  const selectedId = selectedDimensionId();
  const alignmentTargetId = S.ui?.dimensionAlignmentSnap?.dimensionId || null;
  for (const dimension of ensureDimensionState()) {
    renderSingleDimension(layer, dimension, renderCtx, {
      selected: dimension.id === selectedId,
      alignmentTarget: dimension.id === alignmentTargetId
    });
  }
  const preview = previewDimensionFromDraft();
  if (preview) renderSingleDimension(layer, preview, renderCtx, { preview: true });

  if (isDimensionToolActive()) {
    const draft = draftState();
    if (isAngularDimensionToolActive() && draft?.firstSegment) {
      renderDimensionSnapPreview(layer, ctx, { point: draft.pointA, role: SNAP_ROLE.EDGE, candidate: draft.firstSegment });
    }
    const hover = S.ui?.dimensionHover || (draft?.previewPointB
      ? { point: draft.previewPointB, role: normalizeGeometryRef(draft.previewRefB)?.role || SNAP_ROLE.FREE }
      : draft?.pointA
        ? { point: draft.pointA, role: normalizeGeometryRef(draft.refA)?.role || SNAP_ROLE.FREE }
        : null);
    renderDimensionSnapPreview(layer, ctx, hover);
  }
}

export function hitTestDimensionScreen(clientX, clientY) {
  const svg = getPlanRenderTarget();
  const rect = svg?.getBoundingClientRect?.();
  if (!rect) return null;
  const world = pointerWorld({ clientX, clientY });
  if (!world) return null;
  const tolerance = HANDLE_TOLERANCE_PX / Math.max(0.001, numeric(S.camera2D?.scale, 1));
  let best = null;
  for (const dimension of ensureDimensionState()) {
    const resolved = resolveDimensionPoints(dimension);
    const layout = dimensionLayout(dimension, resolved.pointA, resolved.pointB, resolved);
    if (!layout) continue;
    const score = Math.min(
      ...(layout.angular ? layout.lines.map(([a, b]) => distancePointToSegment(world, a, b)) : [distancePointToSegment(world, layout.dimA, layout.dimB)]),
      distance(world, layout.textPosition)
    );
    if (score <= tolerance && (!best || score < best.score)) best = { dimension, score };
  }
  return best?.dimension || null;
}
